import React, { useEffect, useMemo, useRef, useState } from "react";
import { customerbasedshift, shiftadd } from "../../Services/app/masterservice";
import Swal from "sweetalert2";

// Smart Operator: supervisor picks a set of machines that one operator could run
// in parallel. Backend scores feasibility based on per-part cycle times +
// walking distances from the Floor Layout.

const BRAND = {
  orange: "#F99022",
  border: "#e5e7eb",
  bg: "#fafafa",
  textMuted: "#6b7280",
  textDark: "#111827",
  green: "#16a34a",
  red: "#dc2626",
  amber: "#f59e0b",
  blue: "#2563eb",
};

const VERDICT_COLORS = {
  green:  { bg: "#dcfce7", border: "#16a34a", text: "#14532d", emoji: "✅" },
  yellow: { bg: "#fef3c7", border: "#d97706", text: "#7a4a00", emoji: "⚠️" },
  red:    { bg: "#fee2e2", border: "#dc2626", text: "#7a0000", emoji: "🛑" },
};

const GROUP_COLORS = [
  { bg: "#dbeafe", border: "#2563eb" },
  { bg: "#dcfce7", border: "#16a34a" },
  { bg: "#fef3c7", border: "#d97706" },
  { bg: "#fce7f3", border: "#db2777" },
  { bg: "#e0e7ff", border: "#4f46e5" },
  { bg: "#f3e8ff", border: "#9333ea" },
  { bg: "#ccfbf1", border: "#0d9488" },
  { bg: "#ffedd5", border: "#ea580c" },
];
const UNGROUPED_COLOR = { bg: "#f3f4f6", border: "#6b7280" };

function colorFor(groupId) {
  if (!groupId) return UNGROUPED_COLOR;
  let h = 0;
  for (let i = 0; i < groupId.length; i += 1) {
    h = (h * 31 + groupId.charCodeAt(i)) >>> 0;
  }
  return GROUP_COLORS[h % GROUP_COLORS.length];
}

function cleanCustomerId(raw) {
  if (!raw) return "";
  return String(raw).replace(/\\/g, "").replace(/"/g, "").trim();
}

function fmtSeconds(s) {
  if (s == null) return "—";
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return sec ? `${m}m ${sec}s` : `${m}m`;
}

export default function SmartOperator() {
  const baseRef = useRef("");
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [scoring, setScoring] = useState(false);
  const [result, setResult] = useState(null);
  // 'manual' (default — current picker) or 'auto' (system suggests groupings)
  const [tab, setTab] = useState("manual");
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  // ===== Operator assignment workflow state =====
  const [operators, setOperators] = useState([]);              // [{id, operatorname, operatorid}]
  const [assignmentPlan, setAssignmentPlan] = useState({ groups: [] });
  const [assignModalGroup, setAssignModalGroup] = useState(null); // the auto-suggest group being assigned
  const [assignModalOperator, setAssignModalOperator] = useState("");
  const [assignSaving, setAssignSaving] = useState(false);
  const [planSummaryOpen, setPlanSummaryOpen] = useState(false);

  const customerId = useMemo(() => {
    const ls = typeof window !== "undefined" ? localStorage.getItem("CustomerID") : "";
    return cleanCustomerId(ls) || window?._env_?.CUSTOMER_ID || "";
  }, []);

  useEffect(() => {
    baseRef.current = (window._env_?.SERVER_URL2 || "").replace(/\/$/, "");
  }, []);

  useEffect(() => {
    if (!customerId) {
      setErr("no customer logged in");
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        const res = await fetch(
          `${baseRef.current}/api/layout?customer_id=${encodeURIComponent(customerId)}`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setMachines(data.machines || []);
        setErr("");
      } catch (e) {
        setErr(e?.message || "could not load");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [customerId]);

  // Load operator master + saved assignment plan for the Approve/Assign workflow
  useEffect(() => {
    if (!customerId) return;
    const loadOperators = async () => {
      try {
        const r = await customerbasedshift(customerId, 'alloperator');
        const ops = r?.[0]?.value || [];
        setOperators(Array.isArray(ops) ? ops : []);
      } catch { /* non-fatal */ }
    };
    const loadPlan = async () => {
      try {
        const r = await customerbasedshift(customerId, 'operator_assignment_plan');
        const plan = r?.[0]?.value;
        if (plan && typeof plan === 'object') setAssignmentPlan({ groups: plan.groups || [] });
      } catch { /* none yet */ }
    };
    loadOperators();
    loadPlan();
  }, [customerId]);

  // Build a lookup: machine-set fingerprint → assigned operator entry from the saved plan,
  // so any auto-suggest group whose machine set matches an existing assignment renders as ✓ Assigned.
  const assignedByFingerprint = useMemo(() => {
    const map = new Map();
    for (const g of (assignmentPlan?.groups || [])) {
      const fp = (g.machines || []).map(m => m.device_id).sort().join('|');
      map.set(fp, g);
    }
    return map;
  }, [assignmentPlan]);
  const fingerprintForGroup = (g) =>
    (g.per_machine || []).map(m => m.device_id).sort().join('|');

  const saveAssignment = async (group, operator) => {
    if (!operator) { Swal.fire('Pick an operator', '', 'info'); return; }
    setAssignSaving(true);
    try {
      // Fetch machine_specs (image_url etc) for each machine in the group, so
      // the operator's visual brief can show photos straight from the plan.
      const SERVER_URL = (window._env_?.SERVER_URL2 || '').replace(/\/$/, '');
      const machinesList = await Promise.all((group.per_machine || []).map(async m => {
        let image_url = null;
        try {
          const r = await fetch(`${SERVER_URL}/api/operator-assignment/machine-specs?device_id=${m.device_id}`);
          if (r.ok) {
            const specs = await r.json();
            if (specs?.image_url) image_url = specs.image_url;
          }
        } catch { /* non-fatal */ }
        return {
          device_id: m.device_id,
          name: m.name,
          component_code: m.component_code || null,
          image_url,
        };
      }));
      const fp = machinesList.map(m => m.device_id).sort().join('|');
      // The auto-suggest result uses `operator_utilization_pct` and
      // `operator_cycle_seconds` — NOT busy_pct / round_time_min. Read the
      // correct source fields and store under the friendlier names for the brief.
      const utilPct = group.operator_utilization_pct ?? group.busy_pct ?? null;
      const cycleSec = group.operator_cycle_seconds ?? null;
      const roundMin = cycleSec != null ? Math.round(cycleSec / 60) : (group.round_time_min ?? null);
      const entry = {
        group_index: group.group_index ?? null,
        operator_id: operator.operatorid || operator.id || '',
        operator_name: operator.operatorname || operator.name || '',
        machines: machinesList,
        busy_pct: utilPct,
        round_time_min: roundMin,
        assigned_at: Date.now(),
        assigned_by: localStorage.getItem('username') || localStorage.getItem('user') || '',
        fingerprint: fp,
      };
      const existingGroups = (assignmentPlan?.groups || []).filter(g => g.fingerprint !== fp);
      const updatedGroups = [...existingGroups, entry];
      const updatedPlan = { groups: updatedGroups, updated_at: Date.now() };
      await shiftadd({ operator_assignment_plan: updatedPlan }, customerId, 'SERVER_SCOPE');
      setAssignmentPlan(updatedPlan);
      setAssignModalGroup(null);
      setAssignModalOperator("");
      Swal.fire({ icon: 'success', title: 'Assigned', timer: 1300, showConfirmButton: false, toast: true, position: 'top-end' });
    } catch (e) {
      Swal.fire('Save failed', e?.response?.data?.message || e?.message || 'Unknown error', 'error');
    } finally {
      setAssignSaving(false);
    }
  };

  const removeAssignment = async (fingerprint) => {
    const confirmed = await Swal.fire({ icon: 'warning', title: 'Remove assignment?', showCancelButton: true, confirmButtonText: 'Remove' });
    if (!confirmed.isConfirmed) return;
    try {
      const updatedGroups = (assignmentPlan?.groups || []).filter(g => g.fingerprint !== fingerprint);
      const updatedPlan = { groups: updatedGroups, updated_at: Date.now() };
      await shiftadd({ operator_assignment_plan: updatedPlan }, customerId, 'SERVER_SCOPE');
      setAssignmentPlan(updatedPlan);
      Swal.fire({ icon: 'success', title: 'Removed', timer: 900, showConfirmButton: false, toast: true, position: 'top-end' });
    } catch (e) {
      Swal.fire('Remove failed', e?.response?.data?.message || e?.message || 'Unknown error', 'error');
    }
  };

  const printTrainingBrief = (assigned) => {
    const html = `<!doctype html><html><head><title>Training Brief — ${assigned.operator_name}</title>
<style>
  body { font-family: system-ui, sans-serif; padding: 24px; color: #111; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  .sub { color: #6b7280; margin-bottom: 18px; font-size: 13px; }
  .card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 14px; margin-bottom: 12px; }
  .label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th { text-align: left; padding: 6px 8px; background: #f9fafb; border-bottom: 1px solid #e5e7eb; }
  td { padding: 6px 8px; border-bottom: 1px solid #f3f4f6; }
  .badge { display: inline-block; padding: 2px 8px; background: #fef3c7; color: #92400e; border-radius: 3px; font-size: 11px; font-weight: 700; }
</style></head><body>
<h1>🎯 Operator Training Brief</h1>
<div class="sub">Assigned: ${new Date(assigned.assigned_at).toLocaleString('en-IN')} · By: ${assigned.assigned_by || '—'}</div>

<div class="card">
  <div class="label">Operator</div>
  <div style="font-size: 18px; font-weight: 700;">${assigned.operator_name}</div>
  <div style="font-size: 12px; color: #6b7280;">${assigned.operator_id || ''}</div>
</div>

<div class="card">
  <div class="label">Your machines this shift</div>
  <table>
    <thead><tr><th>Machine</th><th>Component</th></tr></thead>
    <tbody>
      ${assigned.machines.map(m => `<tr><td><b>${m.name}</b></td><td>${m.component_code || '—'}</td></tr>`).join('')}
    </tbody>
  </table>
</div>

<div class="card">
  <div class="label">Workload</div>
  Estimated busy time: <span class="badge">${assigned.busy_pct != null ? assigned.busy_pct + '%' : '—'}</span>
  &nbsp;&nbsp; Round time: <b>${assigned.round_time_min ?? '—'} min</b>
</div>

<div class="card" style="background: #f0f9ff; border-color: #0284c7;">
  <div class="label">⚡ Real-time guidance</div>
  Once trained, open <b>Operator Screen</b> on the shop floor tablet. The screen will show live status, current job, and what to do next for each of your machines.
</div>

<script>window.onload = () => setTimeout(() => window.print(), 200);</script>
</body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const m of machines) {
      const key = m.group_id || "__ungrouped__";
      if (!buckets.has(key)) {
        buckets.set(key, {
          groupId: m.group_id,
          groupName: m.group_name || "Ungrouped",
          color: colorFor(m.group_id),
          machines: [],
        });
      }
      buckets.get(key).machines.push(m);
    }
    return Array.from(buckets.values()).sort((a, b) => {
      if (!a.groupId && b.groupId) return 1;
      if (a.groupId && !b.groupId) return -1;
      return a.groupName.localeCompare(b.groupName);
    });
  }, [machines]);

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
    setResult(null);
  };

  const toggleGroup = (bucket) => {
    setSelected((s) => {
      const n = new Set(s);
      const allOn = bucket.machines.every((m) => n.has(m.device_id));
      for (const m of bucket.machines) {
        if (allOn) n.delete(m.device_id);
        else n.add(m.device_id);
      }
      return n;
    });
    setResult(null);
  };

  const clear = () => {
    setSelected(new Set());
    setResult(null);
  };

  const score = async () => {
    if (selected.size < 2) {
      setErr("Select at least 2 machines");
      return;
    }
    if (selected.size > 8) {
      setErr("Limit selection to 8 machines");
      return;
    }
    setScoring(true);
    setErr("");
    try {
      const res = await fetch(`${baseRef.current}/api/operator-assignment/score`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer_id: customerId,
          machine_ids: Array.from(selected),
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.message || `HTTP ${res.status}`);
      setResult(body);
    } catch (e) {
      setErr(e?.message || "scoring failed");
      setResult(null);
    } finally {
      setScoring(false);
    }
  };

  const suggest = async () => {
    setSuggestLoading(true);
    setErr("");
    setSuggestion(null);
    try {
      const res = await fetch(`${baseRef.current}/api/operator-assignment/suggest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer_id: customerId }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.message || `HTTP ${res.status}`);
      setSuggestion(body);
    } catch (e) {
      setErr(e?.message || "could not suggest");
    } finally {
      setSuggestLoading(false);
    }
  };

  // Push a suggested group into the Manual picker
  const applyGroup = (group) => {
    const ids = new Set(group.per_machine.map((m) => m.device_id));
    setSelected(ids);
    setResult(group); // Show as the manual result too
    setTab("manual");
  };

  return (
    <div style={{ padding: "12px 16px 24px", fontFamily: "system-ui, sans-serif" }}>
      {/* Compact header — title + helper + mode tabs on one row */}
      <div style={{
        display: "flex", alignItems: "flex-end", justifyContent: "space-between",
        gap: 16, flexWrap: "wrap", marginBottom: 12,
        paddingBottom: 10, borderBottom: `1px solid ${BRAND.border}`,
      }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: BRAND.textDark, letterSpacing: -0.2 }}>
            Operator Assignment
          </div>
          <div style={{ fontSize: 12, color: BRAND.textMuted, marginTop: 2 }}>
            {tab === "auto"
              ? "Find the smallest number of operators that can safely run all machines."
              : "Pick machines one operator should run in parallel; system scores feasibility."}
          </div>
        </div>
        {/* Inline tab bar — pill style */}
        <div style={{
          display: "inline-flex",
          border: `1px solid ${BRAND.border}`,
          borderRadius: 999,
          padding: 3, background: "#fff",
        }}>
          <button
            onClick={() => setTab("manual")}
            style={{
              padding: "6px 14px", fontSize: 12, fontWeight: 700,
              border: "none", borderRadius: 999,
              background: tab === "manual" ? BRAND.orange : "transparent",
              color: tab === "manual" ? "#fff" : BRAND.textMuted,
              cursor: "pointer",
              transition: "all 120ms ease",
            }}
          >📝 Manual</button>
          <button
            onClick={() => setTab("auto")}
            style={{
              padding: "6px 14px", fontSize: 12, fontWeight: 700,
              border: "none", borderRadius: 999,
              background: tab === "auto" ? BRAND.orange : "transparent",
              color: tab === "auto" ? "#fff" : BRAND.textMuted,
              cursor: "pointer",
              transition: "all 120ms ease",
            }}
          >🤖 Auto-Suggest</button>
        </div>
      </div>

      {err && (
        <div style={{
          background: "#fee2e2", color: BRAND.red, border: `1px solid ${BRAND.red}`,
          padding: "8px 12px", borderRadius: 6, marginBottom: 10, fontSize: 13,
        }}>{err}</div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: BRAND.textMuted }}>
          Loading machines…
        </div>
      ) : tab === "auto" ? (
        <AutoSuggestView
          loading={suggestLoading}
          suggestion={suggestion}
          onSuggest={suggest}
          onApplyGroup={applyGroup}
          operators={operators}
          assignedByFingerprint={assignedByFingerprint}
          fingerprintForGroup={fingerprintForGroup}
          onAssignClick={(g) => { setAssignModalGroup(g); setAssignModalOperator(""); }}
          onRemoveAssignment={removeAssignment}
          onPrintBrief={printTrainingBrief}
          onShowPlanSummary={() => setPlanSummaryOpen(true)}
          assignmentPlan={assignmentPlan}
        />
      ) : (
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          {/* Machine picker */}
          <div
            style={{
              width: 280, flexShrink: 0,
              background: "#fff",
              border: `1px solid ${BRAND.border}`,
              borderRadius: 12,
              padding: "10px 10px 12px",
              maxHeight: 680,
              overflowY: "auto",
              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            }}
          >
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              marginBottom: 10, padding: "2px 4px",
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: BRAND.textMuted, textTransform: "uppercase", letterSpacing: 0.6 }}>
                Machines <span style={{ color: selected.size > 0 ? BRAND.orange : BRAND.textMuted }}>{selected.size}</span><span style={{ color: BRAND.textMuted, fontWeight: 500 }}> / {machines.length}</span>
              </div>
              <button
                onClick={clear}
                disabled={selected.size === 0}
                style={{
                  fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4,
                  background: "transparent",
                  border: "none",
                  color: selected.size === 0 ? "#cbd5e1" : BRAND.blue,
                  cursor: selected.size === 0 ? "not-allowed" : "pointer",
                  padding: 0,
                }}
              >
                Clear
              </button>
            </div>

            {grouped.map((bucket) => {
              const allOn = bucket.machines.every((m) => selected.has(m.device_id));
              return (
                <div key={bucket.groupId || "__ungrouped__"} style={{ marginBottom: 8 }}>
                  <div
                    onClick={() => toggleGroup(bucket)}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "5px 8px",
                      background: bucket.color.bg,
                      border: `1px solid ${bucket.color.border}`,
                      borderRadius: 6, cursor: "pointer", marginBottom: 4,
                    }}
                  >
                    <input type="checkbox" checked={allOn} readOnly style={{ cursor: "pointer" }} />
                    <div style={{
                      flex: 1, fontSize: 12, fontWeight: 700, color: BRAND.textDark,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {bucket.groupName}
                    </div>
                    <span style={{ fontSize: 10, color: BRAND.textMuted, fontWeight: 600 }}>
                      {bucket.machines.length}
                    </span>
                  </div>
                  {/* 2-column compact grid for machines in this group */}
                  <div style={{ marginLeft: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                    {bucket.machines.map((m) => (
                      <div
                        key={m.device_id}
                        onClick={() => toggle(m.device_id)}
                        style={{
                          display: "flex", alignItems: "center", gap: 5,
                          padding: "5px 7px",
                          background: selected.has(m.device_id) ? "#eff6ff" : "#fff",
                          border: `1px solid ${selected.has(m.device_id) ? BRAND.blue : "#f1f5f9"}`,
                          borderRadius: 5, cursor: "pointer",
                          fontSize: 11, fontWeight: 600, color: BRAND.textDark,
                          transition: "all 120ms ease",
                        }}
                      >
                        <input type="checkbox" checked={selected.has(m.device_id)} readOnly style={{ cursor: "pointer", margin: 0 }} />
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={m.name}>
                          {m.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            <button
              onClick={score}
              disabled={selected.size < 2 || scoring}
              style={{
                width: "100%",
                marginTop: 10,
                padding: "10px 16px",
                fontSize: 14, fontWeight: 800,
                borderRadius: 8, border: "none",
                background: selected.size < 2 || scoring ? "#e5e7eb" : BRAND.orange,
                color: selected.size < 2 || scoring ? "#9ca3af" : "#fff",
                cursor: selected.size < 2 || scoring ? "not-allowed" : "pointer",
                boxShadow: selected.size >= 2 && !scoring ? "0 2px 4px rgba(249,144,34,0.25)" : "none",
                transition: "all 150ms ease",
              }}
            >
              {scoring ? "Scoring…" : `Check Assignment${selected.size >= 2 ? ` (${selected.size})` : ""}`}
            </button>
            <div style={{ fontSize: 10, color: BRAND.textMuted, marginTop: 6, textAlign: "center" }}>
              {selected.size === 0 ? "Pick 2–8 machines" :
               selected.size === 1 ? "Pick one more (min 2)" :
               selected.size > 8 ? "⚠ Max 8 machines" :
               `${selected.size} machine${selected.size !== 1 ? "s" : ""} selected`}
            </div>
          </div>

          {/* Result */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {!result && !scoring && (
              <div style={{
                padding: "48px 24px", textAlign: "center", color: BRAND.textMuted,
                fontSize: 14,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 12,
                background: "linear-gradient(180deg, #fff 0%, #fafafa 100%)",
                minHeight: 380,
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14,
              }}>
                <div style={{
                  width: 64, height: 64, borderRadius: "50%",
                  background: "#fff7ed",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 32, color: BRAND.orange,
                  border: `2px solid #fed7aa`,
                }}>👆</div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: BRAND.textDark, marginBottom: 4 }}>
                    Pick 2–8 machines on the left
                  </div>
                  <div style={{ fontSize: 12, color: BRAND.textMuted, maxWidth: 360, lineHeight: 1.5 }}>
                    The system scores whether one operator can run them in parallel — checking cycle times, walking distance, and overlap windows.
                  </div>
                </div>
                <div style={{
                  marginTop: 6, fontSize: 11, color: BRAND.textMuted,
                  padding: "6px 12px", borderRadius: 999, background: "#f9fafb", border: `1px solid ${BRAND.border}`,
                }}>
                  Then tap <b style={{ color: BRAND.orange }}>Check Assignment</b>
                </div>
              </div>
            )}

            {scoring && (
              <div style={{
                padding: 60, textAlign: "center", color: BRAND.textMuted, fontSize: 14,
              }}>
                Computing walking loop and cycle math…
              </div>
            )}

            {result && (
              <ResultCard result={result} />
            )}
          </div>
        </div>
      )}

      {/* ===== Assign Operator modal ===== */}
      {assignModalGroup && (
        <div onClick={() => setAssignModalGroup(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 12, padding: 20, width: 480, maxWidth: '95vw',
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: BRAND.textDark, marginBottom: 4 }}>
              Approve & Assign Operator
            </div>
            <div style={{ fontSize: 12, color: BRAND.textMuted, marginBottom: 14 }}>
              {assignModalGroup.per_machine?.length} machines · {assignModalGroup.busy_pct ?? '—'}% busy · {assignModalGroup.round_time_min ?? '—'} min round
            </div>
            <div style={{
              background: '#f9fafb', border: `1px solid ${BRAND.border}`, borderRadius: 6,
              padding: '8px 10px', marginBottom: 14, fontSize: 12,
            }}>
              {assignModalGroup.per_machine?.map(m => m.name).join(' · ')}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: BRAND.textDark, marginBottom: 6 }}>
              Pick operator
            </div>
            <select
              value={assignModalOperator}
              onChange={(e) => setAssignModalOperator(e.target.value)}
              style={{
                width: '100%', padding: '8px 10px', fontSize: 14,
                border: `1px solid ${BRAND.border}`, borderRadius: 6, marginBottom: 16,
              }}
            >
              <option value="">— select operator —</option>
              {operators?.map((op, idx) => (
                <option key={op.id || op.operatorid || idx} value={op.id || op.operatorid || idx}>
                  {op.operatorname || op.name || `Operator ${idx + 1}`}{op.operatorid ? ` (${op.operatorid})` : ''}
                </option>
              ))}
            </select>
            {(!operators || operators.length === 0) && (
              <div style={{ fontSize: 11, color: BRAND.red, marginTop: -10, marginBottom: 12 }}>
                ⚠ No operators in master. Add some at /operator-registration first.
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setAssignModalGroup(null)} style={{
                padding: '8px 16px', fontSize: 13, fontWeight: 600,
                background: '#fff', border: `1px solid ${BRAND.border}`, borderRadius: 6, cursor: 'pointer',
              }}>Cancel</button>
              <button
                disabled={!assignModalOperator || assignSaving}
                onClick={() => {
                  const op = operators.find(o => String(o.id || o.operatorid) === String(assignModalOperator));
                  if (op) saveAssignment(assignModalGroup, op);
                }}
                style={{
                  padding: '8px 16px', fontSize: 13, fontWeight: 700,
                  background: assignModalOperator && !assignSaving ? BRAND.orange : '#e5e7eb',
                  color: assignModalOperator && !assignSaving ? '#fff' : '#9ca3af',
                  border: 'none', borderRadius: 6,
                  cursor: assignModalOperator && !assignSaving ? 'pointer' : 'not-allowed',
                }}
              >
                {assignSaving ? 'Saving…' : '✓ Approve & Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Plan Summary modal ===== */}
      {planSummaryOpen && (
        <div onClick={() => setPlanSummaryOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 12, padding: 20, width: 720, maxWidth: '95vw',
            maxHeight: '85vh', overflowY: 'auto',
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: BRAND.textDark }}>📋 Saved Assignment Plan</div>
                <div style={{ fontSize: 12, color: BRAND.textMuted, marginTop: 2 }}>
                  {assignmentPlan.groups.length} operator{assignmentPlan.groups.length !== 1 ? 's' : ''} assigned
                  {assignmentPlan.updated_at && ` · Updated ${new Date(assignmentPlan.updated_at).toLocaleString('en-IN')}`}
                </div>
              </div>
              <button onClick={() => setPlanSummaryOpen(false)} style={{
                background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: BRAND.textMuted, padding: 4,
              }}>✕</button>
            </div>
            {assignmentPlan.groups.length === 0 ? (
              <div style={{ padding: 30, textAlign: 'center', color: BRAND.textMuted, fontSize: 13 }}>
                No assignments yet. Approve some auto-suggested groups to build a plan.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: `1px solid ${BRAND.border}`, fontWeight: 700, fontSize: 11 }}>Operator</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: `1px solid ${BRAND.border}`, fontWeight: 700, fontSize: 11 }}>Machines</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: `1px solid ${BRAND.border}`, fontWeight: 700, fontSize: 11 }}>Busy</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: `1px solid ${BRAND.border}`, fontWeight: 700, fontSize: 11 }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: `1px solid ${BRAND.border}`, fontWeight: 700, fontSize: 11 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {assignmentPlan.groups.map((g, idx) => (
                    <tr key={g.fingerprint || idx}>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', fontWeight: 700 }}>{g.operator_name || '—'}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 12 }}>
                        {(g.machines || []).map(m => m.name).join(', ')}
                      </td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 12 }}>
                        {g.busy_pct != null ? `${g.busy_pct}%` : '—'}
                      </td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>
                        {g.acknowledged_at ? (
                          <span title={`Ack'd by ${g.acknowledged_by || g.operator_name} at ${new Date(g.acknowledged_at).toLocaleString('en-IN')}`} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '3px 8px', background: '#dcfce7', color: '#14532d',
                            borderRadius: 999, fontSize: 11, fontWeight: 700,
                          }}>✓ Acknowledged</span>
                        ) : (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '3px 8px', background: '#fef3c7', color: '#92400e',
                            borderRadius: 999, fontSize: 11, fontWeight: 700,
                          }}>⏳ Waiting</span>
                        )}
                      </td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f4f6' }}>
                        <button onClick={() => printTrainingBrief(g)} style={{
                          padding: '4px 10px', fontSize: 11, fontWeight: 700,
                          background: '#fff', border: `1px solid ${BRAND.border}`,
                          borderRadius: 5, cursor: 'pointer', marginRight: 4,
                        }}>📋 Brief</button>
                        <button onClick={() => removeAssignment(g.fingerprint)} style={{
                          padding: '4px 8px', fontSize: 11, fontWeight: 700,
                          background: '#fff', border: `1px solid #fecaca`, color: BRAND.red,
                          borderRadius: 5, cursor: 'pointer',
                        }}>✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AutoSuggestView({ loading, suggestion, onSuggest, onApplyGroup, operators, assignedByFingerprint, fingerprintForGroup, onAssignClick, onRemoveAssignment, onPrintBrief, onShowPlanSummary, assignmentPlan }) {
  const [detailGroup, setDetailGroup] = React.useState(null);

  React.useEffect(() => {
    // Close modal when suggestion refreshes
    setDetailGroup(null);
  }, [suggestion]);

  return (
    <>
      <AutoSuggestPanel
        loading={loading}
        suggestion={suggestion}
        onSuggest={onSuggest}
        onApplyGroup={onApplyGroup}
        onShowDetails={(g, idx) => setDetailGroup({ group: g, index: idx })}
        operators={operators}
        assignedByFingerprint={assignedByFingerprint}
        fingerprintForGroup={fingerprintForGroup}
        onAssignClick={onAssignClick}
        onRemoveAssignment={onRemoveAssignment}
        onPrintBrief={onPrintBrief}
        onShowPlanSummary={onShowPlanSummary}
        assignmentPlan={assignmentPlan}
      />
      {detailGroup && (
        <OperatorDetailsModal
          group={detailGroup.group}
          index={detailGroup.index}
          onClose={() => setDetailGroup(null)}
          onApplyGroup={onApplyGroup}
        />
      )}
    </>
  );
}

function AutoSuggestPanel({ loading, suggestion, onSuggest, onApplyGroup, onShowDetails, operators, assignedByFingerprint, fingerprintForGroup, onAssignClick, onRemoveAssignment, onPrintBrief, onShowPlanSummary, assignmentPlan }) {
  return (
    <div>
      <div
        style={{
          background: "#fff",
          border: `1px solid ${BRAND.border}`,
          padding: "10px 14px",
          borderRadius: 8,
          marginBottom: 10,
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: BRAND.textDark }}>
            How few operators do you need?
          </div>
          <div style={{ fontSize: 12, color: BRAND.textMuted, marginTop: 2 }}>
            Reads cycle times + distances, returns the minimum operator count where each stays ≤ 85% busy.
          </div>
        </div>
        <button
          onClick={onSuggest}
          disabled={loading}
          style={{
            padding: "8px 18px",
            fontSize: 13,
            fontWeight: 700,
            borderRadius: 6,
            border: "none",
            background: loading ? "#9ca3af" : BRAND.orange,
            color: "#fff",
            cursor: loading ? "not-allowed" : "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {loading ? "Computing…" : "Tell me"}
        </button>
      </div>

      {!suggestion && !loading && (
        <div style={{
          padding: 60, textAlign: "center", color: BRAND.textMuted,
          fontSize: 14, border: `1px dashed ${BRAND.border}`, borderRadius: 8,
        }}>
          Tap <b>💡 Tell me</b> above to get the answer.
        </div>
      )}

      {suggestion && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* Compact headline — orange brand accent, single row */}
          <div
            style={{
              background: "#fff",
              border: `1px solid ${BRAND.border}`,
              borderLeft: `4px solid ${BRAND.orange}`,
              borderRadius: 8,
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: BRAND.orange, letterSpacing: 0.8, textTransform: "uppercase" }}>
                Recommendation
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: BRAND.textDark, marginTop: 2 }}>
                <b style={{ fontSize: 22, color: BRAND.orange }}>{suggestion.operator_count}</b> operator{suggestion.operator_count === 1 ? "" : "s"} can run all <b style={{ fontSize: 22 }}>{suggestion.machines_total}</b> machines
                {suggestion.operator_count < suggestion.machines_total && (
                  <span style={{ fontSize: 13, fontWeight: 600, color: BRAND.textMuted, marginLeft: 10 }}>
                    · saves {suggestion.machines_total - suggestion.operator_count} people vs 1:1
                  </span>
                )}
              </div>
              {suggestion.data_source_summary && (
                <div style={{ fontSize: 11, color: BRAND.textMuted, marginTop: 6, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                  <span style={{ fontWeight: 700, color: BRAND.textDark }}>Plan from:</span>
                  {suggestion.data_source_summary.live_component > 0 && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }} title="Live component currently loaded on the machine">
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#16a34a" }} />
                      <b style={{ color: BRAND.textDark }}>{suggestion.data_source_summary.live_component}</b> live
                    </span>
                  )}
                  {suggestion.data_source_summary.comp_attribute > 0 && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }} title="Fell back to comp_operations master baseline (no live event this shift)">
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#9ca3af" }} />
                      <b style={{ color: BRAND.textDark }}>{suggestion.data_source_summary.comp_attribute}</b> master
                    </span>
                  )}
                  {suggestion.data_source_summary.per_part > 0 && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }} title="Per-part observations from machine_status">
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#0ea5e9" }} />
                      <b style={{ color: BRAND.textDark }}>{suggestion.data_source_summary.per_part}</b> observed
                    </span>
                  )}
                  {suggestion.computed_at_ist && (
                    <span style={{ marginLeft: "auto", color: BRAND.textMuted }}>
                      computed {String(suggestion.computed_at_ist).split(",").pop().trim()}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
              <button
                onClick={onSuggest}
                disabled={loading}
                title="Re-fetch live components and recompute"
                style={{
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 700,
                  borderRadius: 6,
                  border: `1px solid ${BRAND.orange}`,
                  background: loading ? "#f3f4f6" : "#fff",
                  color: loading ? BRAND.textMuted : BRAND.orange,
                  cursor: loading ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {loading ? "Recomputing…" : "🔄 Recompute"}
              </button>
              {(assignmentPlan?.groups?.length > 0) && (
                <button
                  onClick={onShowPlanSummary}
                  style={{
                    padding: "6px 12px",
                    fontSize: 12, fontWeight: 700,
                    borderRadius: 6,
                    border: "1px solid #16a34a",
                    background: "#ecfdf5",
                    color: "#14532d",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  📋 View Plan ({assignmentPlan.groups.length})
                </button>
              )}
              <div style={{ fontSize: 11, color: BRAND.textMuted }}>
                Each operator ≤ 85% busy
              </div>
            </div>
          </div>

          {/* Per-operator cards — refined visual */}
          {suggestion.groups.map((g, i) => {
            const v = VERDICT_COLORS[g.verdict] || VERDICT_COLORS.yellow;
            const walkM = Math.round(g.total_walk_m);
            const walkSec = g.total_walk_seconds;
            const serviceMin = Math.round(g.service_seconds_per_cycle / 60);
            const cellName = g.cell_name || "—";
            const loadHint =
              g.operator_utilization_pct < 50 ? "lots of free time" :
              g.operator_utilization_pct < 75 ? "comfortable pace" :
              g.operator_utilization_pct <= 85 ? "busy but doable" :
              "very tight — watch closely";

            // Choose progress bar color by load: orange < 75%, amber 75–85%, red >85%
            const loadColor = g.operator_utilization_pct < 75 ? BRAND.green
              : g.operator_utilization_pct <= 85 ? BRAND.amber : BRAND.red;
            return (
              <div
                key={i}
                style={{
                  background: "#fff",
                  border: `1px solid ${BRAND.border}`,
                  borderRadius: 8,
                  padding: "14px 16px",
                }}
              >
                {/* Top row: operator + cell + busy + see math */}
                <div style={{
                  display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                  marginBottom: 10,
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: "50%",
                    background: BRAND.orange, color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 14, fontWeight: 800, flexShrink: 0,
                  }}>
                    {i + 1}
                  </div>
                  <div style={{
                    fontSize: 15, fontWeight: 800, color: BRAND.textDark,
                  }}>
                    Operator {i + 1}
                  </div>
                  <div style={{
                    padding: "2px 8px", borderRadius: 4,
                    background: "#fef3c7", color: "#7a4a00",
                    fontSize: 11, fontWeight: 700,
                  }}>
                    {cellName}
                  </div>
                  <div style={{
                    fontSize: 12, color: BRAND.textMuted,
                  }}>
                    Runs <b style={{ color: BRAND.textDark }}>{g.per_machine.length}</b> machines
                  </div>
                  <div style={{ flex: 1 }} />
                  {(() => {
                    const fp = fingerprintForGroup ? fingerprintForGroup(g) : null;
                    const assigned = assignedByFingerprint?.get(fp);
                    return assigned ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{
                          padding: "5px 10px", fontSize: 11, fontWeight: 700,
                          background: "#dcfce7", color: "#14532d",
                          borderRadius: 999, border: "1px solid #16a34a",
                        }}>
                          ✓ {assigned.operator_name || 'Assigned'}
                        </span>
                        <button
                          onClick={() => onPrintBrief && onPrintBrief(assigned)}
                          title="Print training brief"
                          style={{
                            padding: "5px 10px", fontSize: 11, fontWeight: 700,
                            background: "#fff", border: `1px solid ${BRAND.border}`,
                            color: BRAND.textDark, borderRadius: 6, cursor: "pointer",
                          }}
                        >📋 Brief</button>
                        <button
                          onClick={() => onRemoveAssignment && onRemoveAssignment(fp)}
                          title="Remove assignment"
                          style={{
                            padding: "5px 8px", fontSize: 11, fontWeight: 700,
                            background: "#fff", border: `1px solid #fecaca`,
                            color: BRAND.red, borderRadius: 6, cursor: "pointer",
                          }}
                        >✕</button>
                      </div>
                    ) : (
                      <button
                        onClick={() => onAssignClick && onAssignClick(g)}
                        style={{
                          padding: "6px 14px", fontSize: 12, fontWeight: 700,
                          background: BRAND.orange, color: "#fff",
                          border: "none", borderRadius: 6, cursor: "pointer",
                          boxShadow: "0 1px 3px rgba(249,144,34,0.3)",
                        }}
                      >
                        ✓ Approve & Assign Operator
                      </button>
                    );
                  })()}
                  <button
                    onClick={() => onShowDetails(g, i)}
                    style={{
                      padding: "6px 12px", marginLeft: 6,
                      fontSize: 12, fontWeight: 700,
                      background: "#fff",
                      border: `1px solid ${BRAND.orange}`,
                      color: BRAND.orange,
                      borderRadius: 6,
                      cursor: "pointer",
                    }}
                  >
                    See the math →
                  </button>
                </div>

                {/* Machine details with current component + reality-check badge */}
                <div style={{ display: "grid", gap: 4, marginBottom: 12 }}>
                  {g.per_machine.map((m) => {
                    const rc = m.reality_check;
                    return (
                      <div key={m.device_id} style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 8,
                        padding: "4px 8px",
                        background: rc ? "#fffbeb" : "#f8fafc",
                        border: rc ? `1px solid ${BRAND.amber}` : "none",
                        borderRadius: 4,
                        fontSize: 12,
                      }}>
                        <span style={{ fontWeight: 700, color: BRAND.textDark, minWidth: 130 }}>
                          {m.name}
                        </span>
                        {m.component_code && (
                          <span style={{ color: BRAND.textMuted, fontSize: 11 }}>
                            <b style={{ color: BRAND.textDark }}>{m.component_code}</b>
                            {m.component_name && ` · ${m.component_name}`}
                          </span>
                        )}
                        <span style={{ flex: 1 }} />
                        {rc && (
                          <span
                            title={rc.message}
                            style={{
                              padding: "1px 6px",
                              background: BRAND.amber,
                              color: "#fff",
                              borderRadius: 3,
                              fontSize: 10,
                              fontWeight: 800,
                              cursor: "help",
                            }}
                          >
                            ⚠ {rc.divergence_pct > 0 ? "+" : ""}{rc.divergence_pct}% off
                          </span>
                        )}
                        <span style={{ color: BRAND.textMuted, fontSize: 10, display: "flex", alignItems: "center", gap: 4 }}>
                          {(() => {
                            const src = m.data_source;
                            const meta = src === "live_component"
                              ? { dot: "#16a34a", label: "live", title: m.live_component_ts_ist ? `Live component set ${m.live_component_ts_ist}` : "From live_component" }
                              : src === "comp_attribute"
                              ? { dot: "#9ca3af", label: "master", title: "From comp_operations master (no live component set this shift)" }
                              : src === "per_part"
                              ? { dot: "#0ea5e9", label: "observed", title: "From per-part run/idle observations" }
                              : src === "operations"
                              ? { dot: "#a16207", label: "from ops", title: "From operations table" }
                              : { dot: "#9ca3af", label: "n/a", title: "No data source" };
                            return (
                              <span title={meta.title} style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                                <span style={{ width: 6, height: 6, borderRadius: "50%", background: meta.dot, display: "inline-block" }} />
                                <span style={{ color: BRAND.textMuted, fontWeight: 600 }}>{meta.label}</span>
                              </span>
                            );
                          })()}
                          <b style={{ color: BRAND.textDark }}>{m.median_cycle_min}m</b>
                          {m.observed_cycle_min != null && rc && (
                            <> · observed <b style={{ color: BRAND.amber }}>{m.observed_cycle_min}m</b></>
                          )}
                          {" "}· handle <b style={{ color: BRAND.textDark }}>{m.median_handle_min}m</b>
                          {m.data_source === "live_component" && m.live_component_ts_ist && (
                            <span style={{ color: BRAND.textMuted, marginLeft: 4 }}>· set {String(m.live_component_ts_ist).split(",").pop().trim()}</span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Inline stats + busy bar */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
                  gap: 14,
                  alignItems: "center",
                }}>
                  <StatInline label="Walking" value={`${walkM} m`} sub={`${walkSec}s`} />
                  <StatInline label="Hands-on" value={`${serviceMin} min`} sub="loading + checks" />
                  <StatInline label="Round time" value={`${Math.round(g.operator_cycle_seconds / 60)} min`} sub="walk + handle" />
                  <div>
                    <div style={{
                      fontSize: 10, color: BRAND.textMuted, fontWeight: 700,
                      textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4,
                    }}>
                      Busy time
                    </div>
                    <div style={{
                      display: "flex", alignItems: "center", gap: 8,
                    }}>
                      <div style={{
                        flex: 1, height: 8, background: "#f3f4f6",
                        borderRadius: 4, overflow: "hidden",
                      }}>
                        <div style={{
                          width: `${Math.min(100, g.operator_utilization_pct)}%`,
                          height: "100%",
                          background: loadColor,
                          transition: "width 400ms",
                        }} />
                      </div>
                      <span style={{ fontSize: 14, fontWeight: 800, color: loadColor }}>
                        {g.operator_utilization_pct}%
                      </span>
                    </div>
                    <div style={{ fontSize: 10, color: BRAND.textMuted, marginTop: 2 }}>
                      {loadHint}
                    </div>
                  </div>
                </div>

                {g.warnings?.length > 0 && (
                  <div style={{
                    marginTop: 10,
                    background: "#fffbeb",
                    border: `1px solid ${BRAND.amber}`,
                    borderRadius: 6,
                    padding: "6px 10px",
                    fontSize: 12, color: "#92400e",
                  }}>
                    {g.warnings.map((w, j) => (
                      <div key={j} style={{ display: "flex", gap: 6, marginBottom: j < g.warnings.length - 1 ? 4 : 0 }}>
                        <span style={{ flexShrink: 0 }}>⚠️</span>
                        <span>{w}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {suggestion.machines_skipped?.length > 0 && (
            <div style={{
              background: "#fee2e2", border: `1px solid ${BRAND.red}`,
              borderRadius: 8, padding: 12, fontSize: 13, color: "#7a0000",
            }}>
              <b>Could not include {suggestion.machines_skipped.length} machine(s)</b> — no cycle data yet:
              {" "}{suggestion.machines_skipped.map((m) => m.name).join(", ")}.
              Run a few parts on those machines, then try again.
            </div>
          )}

          {suggestion.notes?.length > 0 && (
            <div style={{
              background: BRAND.bg, border: `1px solid ${BRAND.border}`,
              borderRadius: 8, padding: 10, fontSize: 12, color: BRAND.textMuted,
            }}>
              {suggestion.notes.map((n, i) => <div key={i}>• {n}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatInline({ label, value, sub }) {
  return (
    <div>
      <div style={{
        fontSize: 10, color: "#6b7280", fontWeight: 700,
        textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2,
      }}>
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 800, color: "#111827", lineHeight: 1.1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function BigStat({ label, value, sub, inverted }) {
  return (
    <div style={{
      background: inverted ? "rgba(255,255,255,0.18)" : "#fff",
      borderRadius: 10,
      padding: "12px 18px",
      textAlign: "center",
      minWidth: 100,
    }}>
      <div style={{ fontSize: 10, fontWeight: 800, opacity: 0.85, letterSpacing: 0.8, color: inverted ? "#fff" : "#0f766e" }}>
        {label}
      </div>
      <div style={{ fontSize: 32, fontWeight: 900, color: inverted ? "#fff" : "#0f766e", lineHeight: 1 }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2, color: inverted ? "#fff" : "#0f766e" }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function MiniStat({ icon, label, value, sub }) {
  return (
    <div style={{ minWidth: 100 }}>
      <div style={{ fontSize: 10, color: "#6b7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>
        {icon && <span style={{ marginRight: 4 }}>{icon}</span>}{label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: "#111827", lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Donut({ percent, color = "#16a34a", bgColor = "#dcfce7", size = 120, stroke = 14 }) {
  const radius = (size - stroke) / 2;
  const c = 2 * Math.PI * radius;
  const p = Math.min(100, Math.max(0, percent));
  const offset = c * (1 - p / 100);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={radius} stroke={bgColor} strokeWidth={stroke} fill="none" />
      <circle
        cx={size/2} cy={size/2} r={radius}
        stroke={color} strokeWidth={stroke} fill="none"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size/2} ${size/2})`}
        style={{ transition: "stroke-dashoffset 400ms ease" }}
      />
      <text
        x="50%" y="50%"
        textAnchor="middle" dominantBaseline="central"
        fontSize="26" fontWeight="800" fill="#111827"
      >
        {Math.round(p)}%
      </text>
    </svg>
  );
}

function OperatorDetailsModal({ group, index, onClose, onApplyGroup }) {
  const v = VERDICT_COLORS[group.verdict] || VERDICT_COLORS.yellow;
  const cellName = group.cell_name || "—";
  const N = group.per_machine.length;

  // ---- Shift activity timeline (fetched on mount) ------------------------
  const [shiftActivity, setShiftActivity] = useState(null);
  const [shiftActivityErr, setShiftActivityErr] = useState("");
  const [timelineZoom, setTimelineZoom] = useState(1); // 1x / 2x / 4x / 8x
  const [hoveredBlock, setHoveredBlock] = useState(null);  // {machine, start, end, dur, kind, x, y}
  const [viewMode, setViewMode] = useState("shift"); // "shift" | "now"
  const [chartMode, setChartMode] = useState("machine"); // "machine" (Actual) | "theoretical" // "attention" | "machine"
  const [nowTick, setNowTick] = useState(Date.now()); // bumps every 30s so "now" view stays fresh
  const [chartFullscreen, setChartFullscreen] = useState(false);
  const [activeTab, setActiveTab] = useState("summary"); // "summary" | "machines" | "activity"
  // Use today 06:00 IST → now as the shift window. PMI shift 1 = 06:00-14:00.
  const SHIFT_START_HOUR_IST = 6;
  const shiftFromMs = (() => {
    // Today's date in IST (en-CA gives YYYY-MM-DD), then anchor at 06:00 IST.
    const istDate = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
    return new Date(`${istDate}T0${SHIFT_START_HOUR_IST}:00:00+05:30`).getTime();
  })();
  const shiftToMs = Date.now();
  const shiftSpanMs = Math.max(1, shiftToMs - shiftFromMs);

  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30 * 1000);
    return () => clearInterval(id);
  }, []);

  // Escape to close chart fullscreen
  useEffect(() => {
    if (!chartFullscreen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setChartFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [chartFullscreen]);

  useEffect(() => {
    let cancelled = false;
    const deviceIds = (group.per_machine || []).map((m) => m.device_id).filter(Boolean).join(",");
    if (!deviceIds) return undefined;
    const base = (window._env_?.SERVER_URL2 || "").replace(/\/$/, "");
    const url = `${base}/api/operator-assignment/shift-activity?device_ids=${encodeURIComponent(deviceIds)}&from=${shiftFromMs}&to=${shiftToMs}`;
    fetch(url)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((d) => { if (!cancelled) setShiftActivity(Array.isArray(d) ? d : []); })
      .catch((e) => { if (!cancelled) setShiftActivityErr(String(e?.message || e)); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.cell_name]);

  // Convert raw machine_status events into idle (hands-on) blocks.
  // Idle codes = 0,1,2; running = 3; alarm = 4,5; locked = 6; setting = 7; disconnected = 100.
  // A "hands-on" block = a contiguous idle period (status in {0,1,2}).
  // Threshold (ms) for "handling" vs "waiting/setup":
  //   short idle (<= 5 min) = operator probably actively handling
  //   long idle  (> 5 min)  = waiting for operator OR setup/alarm-clear
  const HANDLING_MAX_MS = 5 * 60 * 1000;

  // Returns both IDLE blocks (with handle/wait kind) and RUNNING (machining) blocks.
  // Idle codes = 0,1,2; running = 3.
  const machineBlocksFor = (events) => {
    const idle = [];
    const running = [];
    if (!events || !events.length) return { idle, running };
    const isIdle = (s) => s === 0 || s === 1 || s === 2;
    const isRun = (s) => s === 3;
    let currentKind = null;
    let start = null;
    const closeBlock = (closeAt) => {
      if (currentKind === "idle" && start != null) {
        const dur = closeAt - start;
        idle.push({ start, end: closeAt, kind: dur <= HANDLING_MAX_MS ? "handle" : "wait" });
      } else if (currentKind === "running" && start != null) {
        running.push({ start, end: closeAt });
      }
    };
    for (let i = 0; i < events.length; i++) {
      const e = events[i];
      const k = isIdle(e.status) ? "idle" : isRun(e.status) ? "running" : null;
      if (k !== currentKind) {
        closeBlock(e.ts);
        currentKind = k;
        start = e.ts;
      }
    }
    closeBlock(shiftToMs);
    return { idle, running };
  };
  // Backward-compatible shim: callers that want just idle blocks
  const idleBlocksFor = (events) => machineBlocksFor(events).idle;

  // EVENT-DRIVEN simulator: at each step, the operator goes to whichever
  // machine needs attention SOONEST. This produces realistic small waits
  // (cycle finishes a bit before operator returns) instead of the artificial
  // huge waits the round-robin version created. Tie-break uses rotated
  // walking_sequence (longest-cycle machine first).
  //
  // Returns per-machine { handle, cycle, wait } block lists for rendering,
  // matching the actual-chart format.
  const computeTheoretical = (g, fromMs, toMs) => {
    const result = {};
    const list = (g.per_machine || []).slice();
    if (list.length === 0) return result;
    list.forEach((m) => { result[m.device_id] = { handle: [], cycle: [], wait: [] }; });

    // Tie-break order: rotate walking_sequence so longest-cycle machine is first
    const startIdx = list.reduce((best, m, i) =>
      ((m.median_cycle_min || 0) > (list[best].median_cycle_min || 0) ? i : best), 0);
    const startName = list[startIdx].name;
    const seq = (g.walking_sequence || []).slice();
    const ri = seq.indexOf(startName);
    const rotated = ri >= 0 ? seq.slice(ri).concat(seq.slice(0, ri)) : seq;
    const orderOf = {};
    rotated.forEach((name, i) => {
      const m = list.find((x) => x.name === name);
      if (m) orderOf[m.device_id] = i;
    });

    const N = list.length;
    const perLegWalkMs = N > 1 ? ((g.total_walk_seconds || 0) * 1000) / N : 0;

    // Per-machine state:
    //   cycleEndsAt — when current cycle will end (0 = machine never started → needs operator now)
    const state = {};
    list.forEach((m) => { state[m.device_id] = { cycleEndsAt: 0 }; });

    let t = fromMs;
    let safety = 0;
    while (t < toMs && safety < 10000) {
      safety++;
      // Find machines whose cycle has ended (or never started) — they need operator
      const ready = list.filter((m) => state[m.device_id].cycleEndsAt <= t);
      let pick;
      if (ready.length > 0) {
        // Pick the one that has been waiting longest (smallest cycleEndsAt),
        // tie-break by walking order.
        ready.sort((a, b) => {
          const da = state[a.device_id].cycleEndsAt - state[b.device_id].cycleEndsAt;
          if (da !== 0) return da;
          return (orderOf[a.device_id] ?? 0) - (orderOf[b.device_id] ?? 0);
        });
        pick = ready[0];
      } else {
        // All machines cycling — fast-forward t to the soonest finish
        const nextEnd = Math.min(...list.map((m) => state[m.device_id].cycleEndsAt));
        if (!isFinite(nextEnd) || nextEnd >= toMs) break;
        t = nextEnd;
        continue;
      }
      const st = state[pick.device_id];
      // If cycle ended BEFORE operator arrived, record the gap as "wait"
      // (machine sitting idle, operator-induced waste)
      if (st.cycleEndsAt > 0 && st.cycleEndsAt < t) {
        result[pick.device_id].wait.push({
          start: st.cycleEndsAt,
          end: t,
          kind: "wait",
        });
      }
      const handleMs = Math.round((pick.median_handle_min || 0) * 60000);
      const cycleMs = Math.round((pick.median_cycle_min || 0) * 60000);
      // Handle (operator at machine)
      const hStart = t;
      const hEnd = Math.min(toMs, t + handleMs);
      if (hEnd > hStart) {
        result[pick.device_id].handle.push({ start: hStart, end: hEnd, kind: "handle" });
      }
      t = hEnd;
      // Cycle (machine runs unattended)
      const cStart = t;
      const cEnd = Math.min(toMs, t + cycleMs);
      if (cEnd > cStart) {
        result[pick.device_id].cycle.push({ start: cStart, end: cEnd });
      }
      st.cycleEndsAt = cStart + cycleMs;
      // Walk to next (operator moves on while this machine cycles)
      if (perLegWalkMs > 0) {
        t = Math.min(toMs, t + perLegWalkMs);
      }
    }
    return result;
  };

  // Compute the OPERATOR's unified timeline: union all handle blocks across
  // machines (merged where adjacent/overlapping), then derive breathing (free)
  // periods as gaps within the view window.
  const computeOperatorTimeline = (g, fromMs, toMs, mode) => {
    const allHandles = [];
    g.per_machine.forEach((m) => {
      let handles;
      if (mode === "theoretical") {
        const t = computeTheoretical(g, fromMs, toMs)[m.device_id] || { handle: [] };
        handles = t.handle;
      } else {
        const entry = (shiftActivity || []).find((s) => s.deviceId === m.device_id);
        const { idle } = machineBlocksFor(entry?.events || []);
        handles = idle.filter((b) => b.kind === "handle");
      }
      handles.forEach((h) => {
        const s = Math.max(fromMs, h.start);
        const e = Math.min(toMs, h.end);
        if (e > s) allHandles.push({ start: s, end: e });
      });
    });
    // Merge overlapping handle blocks (operator can only be at one place,
    // but cross-machine concurrent handles shouldn't double-count time)
    allHandles.sort((a, b) => a.start - b.start);
    const merged = [];
    for (const h of allHandles) {
      if (merged.length === 0 || h.start > merged[merged.length - 1].end) {
        merged.push({ ...h });
      } else {
        merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, h.end);
      }
    }
    // Derive breathing (free) intervals as gaps in [fromMs, toMs]
    const breathing = [];
    let cursor = fromMs;
    for (const h of merged) {
      if (h.start > cursor) breathing.push({ start: cursor, end: h.start });
      cursor = h.end;
    }
    if (cursor < toMs) breathing.push({ start: cursor, end: toMs });
    return { handles: merged, breathing };
  };

  // Operator-overload analysis: sweep all idle intervals from all machines,
  // accumulate total ms where 2+ machines were idle at the same moment.
  // Sweep-line algorithm:
  //   sort all start/end events, walk through them tracking current count.
  const computeOverlap = (perMachineBlocks) => {
    const points = [];
    for (const arr of perMachineBlocks) {
      for (const b of arr) {
        points.push({ t: b.start, delta: +1 });
        points.push({ t: b.end, delta: -1 });
      }
    }
    points.sort((a, b) => a.t - b.t || a.delta - b.delta);
    let count = 0;
    let lastT = null;
    let overlapMs = 0;
    let overlapEvents = 0;
    let inOverlap = false;
    for (const p of points) {
      if (lastT != null && count >= 2) {
        overlapMs += (p.t - lastT);
      }
      count += p.delta;
      if (count >= 2 && !inOverlap) { inOverlap = true; overlapEvents += 1; }
      else if (count < 2) { inOverlap = false; }
      lastT = p.t;
    }
    return { overlapMs, overlapEvents };
  };

  // ----- View window: "shift" = full shift since 06:00; "now" = last 60 min
  const viewFromMs = viewMode === "now" ? (nowTick - 60 * 60 * 1000) : shiftFromMs;
  const viewToMs   = viewMode === "now" ? nowTick : shiftToMs;
  const viewSpanMs = Math.max(1, viewToMs - viewFromMs);
  const pct = (ms) => `${Math.min(100, Math.max(0, ((ms - viewFromMs) / viewSpanMs) * 100))}%`;
  const wpct = (durMs) => `${Math.min(100, Math.max(0, (durMs / viewSpanMs) * 100))}%`;
  const hourTicks = (() => {
    const arr = [];
    // Tick density: hourly when window > 4h, 15-min when window <= 4h, 5-min when <= 1h.
    const stepMs = viewSpanMs <= 60 * 60 * 1000 ? 5 * 60 * 1000
                 : viewSpanMs <= 4 * 60 * 60 * 1000 ? 15 * 60 * 1000
                 : 60 * 60 * 1000;
    // Round start UP to nearest step boundary
    const start = Math.ceil(viewFromMs / stepMs) * stepMs;
    for (let t = start; t <= viewToMs; t += stepMs) {
      const label = new Date(t).toLocaleTimeString("en-IN", {
        timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false,
      });
      arr.push({ ts: t, label });
    }
    return arr;
  })();

  const opCycleSec = group.operator_cycle_seconds;
  const fastestSec = group.fastest_machine_cycle_seconds;
  const idleSec = Math.max(0, fastestSec - opCycleSec);
  const serviceSec = group.service_seconds_per_cycle;
  const walkSec = group.total_walk_seconds;

  const fmt = (sec) => sec < 60 ? `${Math.round(sec)} s` : `${Math.floor(sec/60)} min ${Math.round(sec%60)} s`;
  const fmtMinShort = (sec) => sec < 60 ? `${Math.round(sec)}s` : `${Math.round(sec/60)} min`;

  // Build a timeline of one operator round in the walking sequence order.
  // For each machine: handling block (orange), then a walking block (grey) to next.
  // Approximate per-machine walking by total_walk_seconds / N for visualization simplicity.
  const stops = group.walking_sequence.map((name) => {
    const m = group.per_machine.find((x) => x.name === name);
    return { name, handle: Math.round((m?.median_handle_min || 0) * 60) };
  });
  const perLegWalk = N > 1 ? walkSec / N : 0;
  const segments = [];
  stops.forEach((s, idx) => {
    segments.push({ kind: "handle", name: s.name, sec: s.handle });
    if (idx < stops.length - 1 || N > 1) {
      segments.push({ kind: "walk", name: `walk → ${stops[(idx + 1) % N].name}`, sec: perLegWalk });
    }
  });
  if (idleSec > 0) {
    segments.push({ kind: "idle", name: "slack (waiting for next machine)", sec: idleSec });
  }
  const totalSec = segments.reduce((a, b) => a + b.sec, 0) || 1;

  // Per-machine fit table data
  const fitRows = group.per_machine.map((m) => {
    const cycleSec = (m.median_cycle_min || 0) * 60;
    const slack = Math.max(0, cycleSec - opCycleSec);
    return {
      name: m.name,
      cycle_min: m.median_cycle_min,
      handle_min: m.median_handle_min,
      slack_min: Math.round(slack / 60),
      fits: cycleSec >= opCycleSec,
      samples: m.samples,
    };
  });

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(15,23,42,0.5)",
        zIndex: 1100,
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "40px 16px",
        overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 14,
          width: "min(960px, 100%)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          overflow: "hidden",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "18px 24px",
          background: "linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)",
          color: "#fff",
          display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: "50%",
            background: "rgba(255,255,255,0.25)", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, fontWeight: 800,
          }}>
            {index + 1}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, opacity: 0.85, letterSpacing: 1, textTransform: "uppercase" }}>
              Operator {index + 1} · Cell {cellName}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, marginTop: 2 }}>
              Why this works — the math behind {N} machines, 1 operator
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 36, height: 36, borderRadius: 8, border: "none",
              background: "rgba(255,255,255,0.2)", color: "#fff",
              fontSize: 20, cursor: "pointer",
            }}
          >✕</button>
        </div>

        <div style={{ padding: "20px 24px 24px", display: "flex", flexDirection: "column", gap: 20 }}>
          {/* The Math */}
          <Section title="The math" subtitle="One operator round = walking + hands-on work">
            <div style={{
              background: "#f8fafc",
              border: `1px solid ${BRAND.border}`,
              borderRadius: 10,
              padding: "16px 18px",
              fontSize: 14,
              color: BRAND.textDark,
              lineHeight: 1.7,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", borderBottom: `1px dashed ${BRAND.border}`, padding: "4px 0" }}>
                <span>🚶 Walking through {N} machines @ 1.2 m/s</span>
                <span><b>{fmt(walkSec)}</b></span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", borderBottom: `1px dashed ${BRAND.border}`, padding: "4px 0" }}>
                <span>🛠 Hands-on (sum of all {N} machines' handling time)</span>
                <span><b>{fmt(serviceSec)}</b></span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0 4px", fontWeight: 800 }}>
                <span>⏱ Total per round</span>
                <span style={{ color: v.border }}>{fmt(opCycleSec)}</span>
              </div>
              <div style={{ marginTop: 10, padding: "10px 12px", background: v.bg, borderRadius: 6, color: v.text, fontSize: 13 }}>
                The fastest machine in this group has a cycle of <b>{fmtMinShort(fastestSec)}</b>.
                {" "}So <b>{fmtMinShort(opCycleSec)} ÷ {fmtMinShort(fastestSec)} = {group.operator_utilization_pct}%</b> — the operator is busy {group.operator_utilization_pct}% of the time,
                {" "}with <b>{fmtMinShort(idleSec)}</b> of breathing room per round before the fastest machine needs them again.
              </div>
            </div>
          </Section>

          {/* Tab bar — split heavy content into focused views */}
          <div style={{
            display: "flex", gap: 4,
            borderBottom: "1px solid " + BRAND.border,
            marginBottom: 16,
          }}>
            {[
              { id: "summary",  label: "Summary" },
              { id: "machines", label: "Per machine" },
              { id: "activity", label: "Shift activity" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                style={{
                  padding: "10px 18px",
                  background: "transparent",
                  color: activeTab === t.id ? BRAND.orange : BRAND.textMuted,
                  border: "none",
                  borderBottom: "3px solid " + (activeTab === t.id ? BRAND.orange : "transparent"),
                  cursor: "pointer",
                  fontWeight: 800, fontSize: 14,
                  marginBottom: -1,
                }}
              >{t.label}</button>
            ))}
          </div>

          {/* SUMMARY TAB */}
          {activeTab === "summary" && <>
          {/* Timeline */}
          <Section title="One round, visualized" subtitle="Each block shows the operator's activity in time">
            <div style={{
              display: "flex", height: 56,
              borderRadius: 8, overflow: "hidden",
              border: `1px solid ${BRAND.border}`,
            }}>
              {segments.map((seg, idx) => {
                const w = (seg.sec / totalSec) * 100;
                const bg = seg.kind === "handle" ? v.border : seg.kind === "walk" ? "#94a3b8" : "#e5e7eb";
                const fg = seg.kind === "idle" ? "#6b7280" : "#fff";
                return (
                  <div
                    key={idx}
                    title={`${seg.name} — ${fmt(seg.sec)}`}
                    style={{
                      width: `${w}%`,
                      background: bg,
                      color: fg,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11, fontWeight: 700,
                      borderRight: idx < segments.length - 1 ? "1px solid rgba(255,255,255,0.4)" : "none",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      padding: "0 4px",
                    }}
                  >
                    {w > 6 && (seg.kind === "handle"
                      ? seg.name.split("-").slice(-2).join("-")
                      : seg.kind === "walk"
                        ? "·"
                        : "slack")}
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 18, marginTop: 10, fontSize: 12, color: BRAND.textMuted, flexWrap: "wrap" }}>
              <LegendDot color={v.border} label="Hands-on at a machine" />
              <LegendDot color="#94a3b8" label="Walking between machines" />
              <LegendDot color="#e5e7eb" label="Slack (operator free time)" />
            </div>
          </Section>

          </>}

          {/* PER MACHINE TAB */}
          {activeTab === "machines" && <>
          {/* Per-machine fit */}
          <Section title="Per-machine fit" subtitle="Each machine's cycle compared to the operator's round trip">
            <div style={{ overflowX: "auto", border: `1px solid ${BRAND.border}`, borderRadius: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8fafc", color: BRAND.textMuted, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700 }}>Machine</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700 }}>Cycle</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700 }}>Hands-on</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700 }}>Slack</th>
                    <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: 700 }}>Fits?</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700 }}>Samples</th>
                  </tr>
                </thead>
                <tbody>
                  {fitRows.map((r) => (
                    <tr key={r.name} style={{ borderTop: `1px solid ${BRAND.border}` }}>
                      <td style={{ padding: "10px 12px", fontWeight: 700, color: BRAND.textDark }}>{r.name}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right" }}>{r.cycle_min} min</td>
                      <td style={{ padding: "10px 12px", textAlign: "right" }}>{r.handle_min} min</td>
                      <td style={{ padding: "10px 12px", textAlign: "right", color: r.slack_min > 0 ? BRAND.green : BRAND.red }}>
                        {r.slack_min > 0 ? `+${r.slack_min} min` : `${r.slack_min} min`}
                      </td>
                      <td style={{ padding: "10px 12px", textAlign: "center" }}>
                        {r.fits
                          ? <span style={{ color: BRAND.green, fontWeight: 800 }}>✓</span>
                          : <span style={{ color: BRAND.red, fontWeight: 800 }}>✗</span>}
                      </td>
                      <td style={{ padding: "10px 12px", textAlign: "right", color: BRAND.textMuted, fontSize: 12 }}>{r.samples}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 12, color: BRAND.textMuted, marginTop: 8 }}>
              <b>Slack</b> = how long the machine could keep running before the operator must return.
              {" "}Positive slack means the machine doesn't wait. Negative slack means the operator can't keep up.
            </div>
          </Section>

          {/* Recommended start sequence — longest-cycle machine first so it runs
              unattended while operator handles the shorter-cycle machines */}
          {(() => {
            const list = (group.per_machine || []).slice();
            if (list.length < 1) return null;
            // Pick start: longest median_cycle_min
            const startIdx = list.reduce((best, m, i) =>
              ((m.median_cycle_min || 0) > (list[best].median_cycle_min || 0) ? i : best), 0);
            const startName = list[startIdx].name;
            // Rotate walking_sequence so the start machine is first
            const seq = (group.walking_sequence || []).slice();
            const ri = seq.indexOf(startName);
            const rotated = ri >= 0 ? seq.slice(ri).concat(seq.slice(0, ri)) : seq;
            const startCycle = Math.round(list[startIdx].median_cycle_min || 0);
            return (
              <Section
                title="Recommended start order"
                subtitle="Start the longest-cycle machine first so it runs unattended while you handle the others, then walk the optimal loop."
              >
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
                  {rotated.map((name, i) => {
                    const m = list.find((x) => x.name === name);
                    const cycleMin = Math.round(m?.median_cycle_min || 0);
                    const handleMin = Math.round(m?.median_handle_min || 0);
                    const isStart = i === 0;
                    return (
                      <React.Fragment key={name}>
                        <div style={{
                          display: "inline-flex", alignItems: "center", gap: 8,
                          background: isStart ? BRAND.green : "#fff",
                          color: isStart ? "#fff" : BRAND.textDark,
                          border: `2px solid ${isStart ? BRAND.green : BRAND.border}`,
                          borderRadius: 999,
                          padding: "8px 14px 8px 8px",
                          minHeight: 40,
                          boxShadow: isStart ? `0 4px 12px ${BRAND.green}40` : "none",
                        }}>
                          <span style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: 24, height: 24, borderRadius: "50%",
                            background: isStart ? "#fff" : BRAND.green,
                            color: isStart ? BRAND.green : "#fff",
                            fontSize: 13, fontWeight: 900,
                          }}>{i + 1}</span>
                          <span style={{ fontWeight: 800, fontSize: 14 }}>{name}</span>
                          <span style={{
                            fontSize: 11,
                            color: isStart ? "rgba(255,255,255,0.85)" : BRAND.textMuted,
                            fontWeight: 600,
                          }}>
                            {cycleMin}m cycle · {handleMin}m hands-on
                          </span>
                          {isStart && (
                            <span style={{
                              fontSize: 10, fontWeight: 800,
                              padding: "2px 8px",
                              background: "rgba(255,255,255,0.25)",
                              color: "#fff",
                              borderRadius: 4, letterSpacing: 0.5,
                            }}>START</span>
                          )}
                        </div>
                        {i < rotated.length - 1 && (
                          <span style={{ color: BRAND.textMuted, fontSize: 18, fontWeight: 800 }}>→</span>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {rotated.length > 1 && (
                    <>
                      <span style={{ color: BRAND.textMuted, fontSize: 14 }}>↩ loop</span>
                    </>
                  )}
                </div>
                <div style={{ fontSize: 12, color: BRAND.textMuted, marginTop: 10 }}>
                  <b>Why {startName} first:</b> longest cycle ({startCycle} min) →
                  starts it once, then it runs unattended while you do the others.
                  This minimizes the amount of time any machine sits idle waiting for the operator.
                </div>
              </Section>
            );
          })()}

          </>}

          {/* ACTIVITY TAB */}
          {activeTab === "activity" && <>
          {/* Shift activity timeline — what the operator actually did today */}
          <Section
            title={
              chartMode === "theoretical"
                ? "Theoretical operator day"
                : "Shift activity today"
            }
            subtitle={`${
              chartMode === "theoretical"
                ? "Ideal day simulated from declared cycle + handle + walking"
                : "What actually happened — machining + load/unload + attention"
            } — ${new Date(viewFromMs).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false })} → ${viewMode === "now" ? "now (live)" : new Date(viewToMs).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false })}`}
          >
            {shiftActivityErr && (
              <div style={{ color: BRAND.red, fontSize: 13, marginBottom: 8 }}>
                Couldn't load shift activity: {shiftActivityErr}
              </div>
            )}
            {!shiftActivity && !shiftActivityErr && (
              <div style={{ color: BRAND.textMuted, fontSize: 13 }}>Loading…</div>
            )}
            {shiftActivity && shiftActivity.length === 0 && (
              <div style={{ color: BRAND.textMuted, fontSize: 13 }}>No machine_status events for this shift window.</div>
            )}
            {shiftActivity && shiftActivity.length > 0 && (
              <div
                onClick={(e) => {
                  // Don't open fullscreen if click started on a button/control
                  const tag = (e.target.tagName || "").toLowerCase();
                  if (tag === "button" || e.target.closest("button")) return;
                  setChartFullscreen(true);
                }}
                title="Click to expand"
                style={{
                  border: `1px solid ${BRAND.border}`, borderRadius: 8,
                  padding: 12, background: "#fafafa",
                  cursor: "zoom-in",
                  position: "relative",
                }}>
                {/* Expand hint */}
                <div style={{
                  position: "absolute", top: 8, right: 12,
                  fontSize: 11, color: BRAND.textMuted, fontWeight: 700,
                  background: "#fff", padding: "2px 8px", borderRadius: 4,
                  border: `1px solid ${BRAND.border}`,
                  display: "flex", alignItems: "center", gap: 4,
                  pointerEvents: "none",
                }}>
                  ⛶ Click to expand
                </div>
                {/* Zoom controls */}
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, fontSize: 12, flexWrap: "wrap" }}>
                  <span style={{ color: BRAND.textMuted, fontWeight: 700 }}>View:</span>
                  {[
                    { id: "shift", label: "Shift" },
                    { id: "now", label: "Now (last 1h)" },
                  ].map((v) => (
                    <button
                      key={v.id}
                      onClick={() => { setViewMode(v.id); setTimelineZoom(1); }}
                      style={{
                        padding: "4px 12px",
                        background: viewMode === v.id ? BRAND.green : "#fff",
                        color: viewMode === v.id ? "#fff" : BRAND.textDark,
                        border: `1px solid ${viewMode === v.id ? BRAND.green : BRAND.border}`,
                        borderRadius: 4,
                        cursor: "pointer",
                        fontWeight: 700,
                        fontSize: 12,
                      }}
                    >{v.label}</button>
                  ))}
                  <span style={{ width: 1, height: 20, background: BRAND.border, margin: "0 4px" }} />
                  <span style={{ color: BRAND.textMuted, fontWeight: 700 }}>Chart:</span>
                  {[
                    { id: "machine", label: "Actual" },
                    { id: "theoretical", label: "Theoretical" },
                  ].map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setChartMode(c.id)}
                      style={{
                        padding: "4px 12px",
                        background: chartMode === c.id ? BRAND.green : "#fff",
                        color: chartMode === c.id ? "#fff" : BRAND.textDark,
                        border: `1px solid ${chartMode === c.id ? BRAND.green : BRAND.border}`,
                        borderRadius: 4,
                        cursor: "pointer",
                        fontWeight: 700,
                        fontSize: 12,
                      }}
                    >{c.label}</button>
                  ))}
                  <span style={{ width: 1, height: 20, background: BRAND.border, margin: "0 4px" }} />
                  <span style={{ color: BRAND.textMuted, fontWeight: 700 }}>Zoom:</span>
                  {[1, 2, 4, 8].map((z) => (
                    <button
                      key={z}
                      onClick={() => setTimelineZoom(z)}
                      style={{
                        padding: "4px 10px",
                        background: timelineZoom === z ? BRAND.green : "#fff",
                        color: timelineZoom === z ? "#fff" : BRAND.textDark,
                        border: `1px solid ${timelineZoom === z ? BRAND.green : BRAND.border}`,
                        borderRadius: 4,
                        cursor: "pointer",
                        fontWeight: 700,
                        fontSize: 12,
                      }}
                    >{z}x</button>
                  ))}
                  <span style={{ color: BRAND.textMuted, marginLeft: 8 }}>
                    {timelineZoom > 1 ? "scroll horizontally to pan →" : "full shift visible"}
                  </span>
                </div>



                {/* Scrollable timeline container — inner width scales with zoom */}
                <div style={{ overflowX: "auto", paddingBottom: 4 }}>
                <div style={{ width: `${timelineZoom * 100}%`, minWidth: "100%" }}>
                {/* Hour ticks header */}
                <div style={{ position: "relative", height: 20, marginLeft: 120, marginBottom: 6, borderBottom: `1px solid ${BRAND.border}` }}>
                  {hourTicks.map((t) => (
                    <div key={t.ts} style={{
                      position: "absolute", left: pct(t.ts),
                      fontSize: 10, color: BRAND.textMuted, fontWeight: 700,
                      transform: "translateX(-50%)",
                    }}>{t.label}</div>
                  ))}
                </div>
                {/* Operator row — handling + breathing */}
                {(() => {
                  const opT = computeOperatorTimeline(group, viewFromMs, viewToMs, chartMode);
                  const totalBreathMs = opT.breathing.reduce((a, b) => a + (b.end - b.start), 0);
                  const breathPct = Math.round((totalBreathMs / viewSpanMs) * 100);
                  return (
                    <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                      <div style={{
                        width: 120, paddingRight: 8, fontSize: 12, fontWeight: 800,
                        color: BRAND.orange, whiteSpace: "nowrap",
                      }}>👤 Operator</div>
                      <div style={{
                        position: "relative", flex: 1, height: 18,
                        background: "#fce7f3", borderRadius: 4, overflow: "hidden",
                      }}>
                        {opT.breathing.map((b, i) => (
                          <div key={"ob-" + i}
                            onMouseEnter={(e) => {
                              const r = e.currentTarget.getBoundingClientRect();
                              setHoveredBlock({
                                machine: "Operator",
                                start: b.start, end: b.end,
                                dur: b.end - b.start, kind: "breathing",
                                x: r.left + r.width / 2, y: r.top,
                              });
                            }}
                            onMouseLeave={() => setHoveredBlock(null)}
                            style={{
                              position: "absolute", top: 0, bottom: 0,
                              left: pct(b.start), width: wpct(b.end - b.start),
                              background: "#fbcfe8", cursor: "pointer",
                            }} />
                        ))}
                        {opT.handles.map((h, i) => (
                          <div key={"oh-" + i}
                            onMouseEnter={(e) => {
                              const r = e.currentTarget.getBoundingClientRect();
                              setHoveredBlock({
                                machine: "Operator",
                                start: h.start, end: h.end,
                                dur: h.end - h.start, kind: "handling",
                                x: r.left + r.width / 2, y: r.top,
                              });
                            }}
                            onMouseLeave={() => setHoveredBlock(null)}
                            style={{
                              position: "absolute", top: 0, bottom: 0,
                              left: pct(h.start), width: wpct(h.end - h.start),
                              background: "#facc15", cursor: "pointer",
                            }} />
                        ))}
                      </div>
                      <div style={{ width: 130, paddingLeft: 10, fontSize: 10, color: BRAND.textMuted, lineHeight: 1.3 }}>
                        <div style={{ fontSize: 16, fontWeight: 900, color: "#ec4899", lineHeight: 1 }}>{breathPct}%</div>
                        <div style={{ fontSize: 10, color: BRAND.textMuted, fontWeight: 700, marginTop: 1 }}>breathing</div>
                      </div>
                    </div>
                  );
                })()}
                {/* One row per machine */}
                {(() => null)()}
                {(() => {
                  // Sort per-machine rows by first-start in view window.
                  const list = (group.per_machine || []).slice();
                  const firstMs = {};
                  if (chartMode === "theoretical") {
                    const theo = computeTheoretical(group, viewFromMs, viewToMs);
                    list.forEach((m) => {
                      const t = theo[m.device_id] || { handle: [], cycle: [] };
                      const all = t.handle.concat(t.cycle);
                      firstMs[m.device_id] = all.length ? Math.min(...all.map((b) => b.start)) : Infinity;
                    });
                  } else {
                    list.forEach((m) => {
                      const entry = (shiftActivity || []).find((s) => s.deviceId === m.device_id);
                      const ev = (entry?.events || []).filter((e) => e.ts >= viewFromMs && e.ts <= viewToMs);
                      firstMs[m.device_id] = ev.length ? ev[0].ts : Infinity;
                    });
                  }
                  list.sort((a, b) => firstMs[a.device_id] - firstMs[b.device_id]);
                  return list;
                })().map((m) => {
                  let blocks, runBlocks;
                  if (chartMode === "theoretical") {
                    const t = computeTheoretical(group, viewFromMs, viewToMs)[m.device_id] || { handle: [], cycle: [], wait: [] };
                    blocks = t.handle.concat(t.wait);
                    runBlocks = t.cycle;
                  } else {
                    const entry = shiftActivity.find((s) => s.deviceId === m.device_id);
                    const { idle: idleAll, running: runAll } = machineBlocksFor(entry?.events || []);
                    blocks = idleAll.filter((b) => b.end > viewFromMs && b.start < viewToMs);
                    runBlocks = runAll.filter((b) => b.end > viewFromMs && b.start < viewToMs);
                  }
                  const totalIdleMs = blocks.reduce(
                    (a, b) => a + (Math.min(b.end, viewToMs) - Math.max(b.start, viewFromMs)), 0
                  );
                  const totalRunMs = runBlocks.reduce(
                    (a, b) => a + (Math.min(b.end, viewToMs) - Math.max(b.start, viewFromMs)), 0
                  );
                  const totalIdleMin = Math.round(totalIdleMs / 60000);
                  // Standard times from live_component (engineered plan)
                  const stdCycleMin = Math.round(m.median_cycle_min || 0);
                  const stdHandleMin = Math.round((m.median_handle_min || 0) * 10) / 10;
                  // Actual averages from today's blocks
                  const handleBlocks = blocks.filter((b) => b.kind === "handle");
                  const avgHandleMin = handleBlocks.length > 0
                    ? Math.round((handleBlocks.reduce((a, b) => a + (b.end - b.start), 0) / handleBlocks.length / 60000) * 10) / 10
                    : 0;
                  const avgCycleMin = runBlocks.length > 0
                    ? Math.round((runBlocks.reduce((a, b) => a + (b.end - b.start), 0) / runBlocks.length / 60000) * 10) / 10
                    : 0;
                  return (
                    <div key={m.device_id} style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
                      <div style={{
                        width: 120, paddingRight: 8, fontSize: 12, fontWeight: 700,
                        color: BRAND.textDark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }} title={m.name}>{m.name}</div>
                      <div style={{
                        position: "relative", flex: 1, height: 22,
                        background: "#e5e7eb", borderRadius: 4, overflow: "hidden",
                      }}>
                        {/* Machining (running) blocks — only shown in machine-status mode */}
                        {(chartMode === "machine" || chartMode === "theoretical") && runBlocks.map((b, i) => (
                          <div
                            key={"run-" + i}
                            onMouseEnter={(e) => {
                              const r = e.currentTarget.getBoundingClientRect();
                              setHoveredBlock({
                                machine: m.name, start: b.start, end: b.end,
                                dur: b.end - b.start, kind: "machining",
                                x: r.left + r.width / 2, y: r.top,
                              });
                            }}
                            onMouseLeave={() => setHoveredBlock(null)}
                            style={{
                              position: "absolute", top: 0, bottom: 0,
                              left: pct(b.start), width: wpct(b.end - b.start),
                              background: "#16a34a", cursor: "pointer",
                            }}
                          />
                        ))}
                        {blocks.map((b, i) => {
                          const dur = b.end - b.start;
                          const isHandle = b.kind === "handle";
                          const color = isHandle ? "#facc15" : "#3b82f6";
                          return (
                            <div
                              key={i}
                              onMouseEnter={(e) => {
                                const r = e.currentTarget.getBoundingClientRect();
                                setHoveredBlock({
                                  machine: m.name,
                                  start: b.start,
                                  end: b.end,
                                  dur,
                                  kind: b.kind,
                                  x: r.left + r.width / 2,
                                  y: r.top,
                                });
                              }}
                              onMouseLeave={() => setHoveredBlock(null)}
                              style={{
                                position: "absolute", top: 0, bottom: 0,
                                left: pct(b.start), width: wpct(dur),
                                background: color,
                                cursor: "pointer",
                              }}
                            />
                          );
                        })}
                      </div>
                      {(() => {
                        const utilPct = Math.round((totalRunMs / viewSpanMs) * 100);
                        const utilColor = utilPct >= 80 ? BRAND.green : utilPct >= 50 ? "#f59e0b" : BRAND.red;
                        return (
                          <div style={{ width: 130, paddingLeft: 10, fontSize: 10, color: BRAND.textMuted, lineHeight: 1.3 }}>
                            <div style={{ fontSize: 18, fontWeight: 900, color: utilColor, lineHeight: 1 }}>{utilPct}%</div>
                            <div style={{ fontSize: 10, color: BRAND.textMuted, fontWeight: 700, marginTop: 1 }}>utilization</div>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
                </div>
                </div>
                {/* end scrollable timeline */}
                <div style={{ display: "flex", gap: 14, marginTop: 10, fontSize: 11, color: BRAND.textMuted, flexWrap: "wrap" }}>
                  {chartMode === "machine" && <LegendDot color="#16a34a" label="Machining (machine running)" />}
                  <LegendDot color="#facc15" label="Loading / Unloading (≤5 min)" />
                  <LegendDot color="#3b82f6" label="Attention needed (>5 min)" />
                  <LegendDot color="#e5e7eb" label={chartMode === "machine" ? "Idle / no data" : "Machine running / no data"} />
                </div>

              </div>
            )}
          </Section>

          {/* Cycle bars — visual comparison */}
          <Section title="Cycle time comparison" subtitle="How long each machine takes for one part, vs the operator's round">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(() => {
                const maxMin = Math.max(
                  ...group.per_machine.map((m) => m.median_cycle_min || 0),
                  opCycleSec / 60,
                );
                return (
                  <>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 140, fontSize: 12, fontWeight: 700, color: v.border }}>
                        Operator round
                      </div>
                      <div style={{ flex: 1, background: "#f1f5f9", borderRadius: 4, height: 22, position: "relative", overflow: "hidden" }}>
                        <div style={{
                          height: "100%",
                          width: `${((opCycleSec / 60) / maxMin) * 100}%`,
                          background: v.border,
                          display: "flex", alignItems: "center",
                          paddingLeft: 8, color: "#fff", fontSize: 11, fontWeight: 700,
                        }}>
                          {Math.round(opCycleSec / 60)} min
                        </div>
                      </div>
                    </div>
                    {group.per_machine.map((m) => {
                      const cm = m.median_cycle_min || 0;
                      const fits = cm * 60 >= opCycleSec;
                      return (
                        <div key={m.device_id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 140, fontSize: 12, color: BRAND.textDark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {m.name}
                          </div>
                          <div style={{ flex: 1, background: "#f1f5f9", borderRadius: 4, height: 22, position: "relative", overflow: "hidden" }}>
                            <div style={{
                              height: "100%",
                              width: `${(cm / maxMin) * 100}%`,
                              background: fits ? "#10b981" : "#ef4444",
                              display: "flex", alignItems: "center",
                              paddingLeft: 8, color: "#fff", fontSize: 11, fontWeight: 700,
                            }}>
                              {cm} min
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>
            <div style={{ fontSize: 12, color: BRAND.textMuted, marginTop: 8 }}>
              Each green bar is a machine cycle. As long as it's <b>longer</b> than the operator's round (the colored one at top), the operator stays ahead.
            </div>
          </Section>

          {/* Risks */}
          <Section title="What could go wrong" subtitle="">
            {group.warnings?.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {group.warnings.map((w, j) => (
                  <div key={j} style={{
                    background: "#fffbeb",
                    border: `1px solid ${BRAND.amber}`,
                    borderRadius: 8,
                    padding: "10px 14px",
                    fontSize: 13,
                    color: "#92400e",
                    display: "flex", gap: 10,
                  }}>
                    <span style={{ flexShrink: 0 }}>⚠️</span>
                    <span>{w}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: BRAND.textMuted }}>
                No risks detected — cycle times don't sync up and the operator has comfortable slack.
              </div>
            )}
            {group.notes?.length > 0 && group.notes.map((n, j) => (
              <div key={j} style={{
                marginTop: 8,
                background: BRAND.bg,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 6,
                padding: "8px 12px",
                fontSize: 12,
                color: BRAND.textMuted,
              }}>
                {n}
              </div>
            ))}
          </Section>
          </>}
          {/* end tabs */}

          {/* Footer actions */}
          <div style={{
            display: "flex", justifyContent: "flex-end", gap: 8,
            paddingTop: 12,
            borderTop: `1px solid ${BRAND.border}`,
          }}>
            <button
              onClick={onClose}
              style={{
                padding: "10px 18px",
                fontSize: 13, fontWeight: 700,
                background: "#fff",
                border: `1px solid ${BRAND.border}`,
                color: BRAND.textDark,
                borderRadius: 8, cursor: "pointer",
              }}
            >
              Close
            </button>
            <button
              onClick={() => { onApplyGroup(group); onClose(); }}
              style={{
                padding: "10px 18px",
                fontSize: 13, fontWeight: 700,
                background: BRAND.orange,
                border: "none",
                color: "#fff",
                borderRadius: 8, cursor: "pointer",
              }}
            >
              Open in Manual to tweak →
            </button>
          </div>
        </div>
      </div>

      {/* Chart fullscreen overlay — click anywhere to close, or use the X */}
      {chartFullscreen && shiftActivity && (() => {
        const allBlocks = group.per_machine.map((m) => {
          const entry = shiftActivity.find((s) => s.deviceId === m.device_id);
          return idleBlocksFor(entry?.events || []);
        });
        const { overlapMs, overlapEvents } = computeOverlap(allBlocks);
        const pctOfShift = (overlapMs / shiftSpanMs) * 100;
        return (
          <div
            onClick={(e) => {
              // stop propagation so clicks INSIDE the fullscreen never reach
              // OperatorDetailsModal's outer onClose handler.
              e.stopPropagation();
              if (e.target === e.currentTarget) setChartFullscreen(false);
            }}
            style={{
              position: "fixed", inset: 0,
              background: "rgba(15,23,42,0.75)",
              zIndex: 1200,
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 24,
              fontFamily: "system-ui, sans-serif",
            }}
          >
            <div style={{
              background: "#fff",
              borderRadius: 12,
              width: "100%", height: "100%",
              maxWidth: 1600, maxHeight: "95vh",
              boxShadow: "0 24px 60px rgba(0,0,0,0.45)",
              padding: 24,
              display: "flex", flexDirection: "column",
              gap: 16,
              overflow: "hidden",
            }}>
              {/* Header bar */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: BRAND.textDark }}>
                    Shift activity — {cellName}
                  </div>
                  <div style={{ fontSize: 13, color: BRAND.textMuted, marginTop: 2 }}>
                    Window: {new Date(viewFromMs).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false })}
                    {" → "}
                    {viewMode === "now" ? "now (live, refreshing every 30s)" : new Date(viewToMs).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false })}
                  </div>
                </div>
                <button
                  onClick={() => setChartFullscreen(false)}
                  style={{
                    width: 40, height: 40, borderRadius: "50%",
                    background: "#f1f5f9", color: BRAND.textDark,
                    border: "none", cursor: "pointer",
                    fontSize: 20, fontWeight: 800,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                  title="Close (Esc)"
                >×</button>
              </div>

              {/* Controls bar */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, flexWrap: "wrap" }}>
                <span style={{ color: BRAND.textMuted, fontWeight: 700 }}>View:</span>
                {[
                  { id: "shift", label: "Shift" },
                  { id: "now", label: "Now (last 1h)" },
                ].map((v) => (
                  <button
                    key={v.id}
                    onClick={() => { setViewMode(v.id); setTimelineZoom(1); }}
                    style={{
                      padding: "6px 16px",
                      background: viewMode === v.id ? BRAND.green : "#fff",
                      color: viewMode === v.id ? "#fff" : BRAND.textDark,
                      border: `1px solid ${viewMode === v.id ? BRAND.green : BRAND.border}`,
                      borderRadius: 6, cursor: "pointer", fontWeight: 700, fontSize: 13,
                    }}
                  >{v.label}</button>
                ))}
                <span style={{ width: 1, height: 22, background: BRAND.border, margin: "0 8px" }} />
                <span style={{ color: BRAND.textMuted, fontWeight: 700 }}>Zoom:</span>
                {[1, 2, 4, 8].map((z) => (
                  <button
                    key={z}
                    onClick={() => setTimelineZoom(z)}
                    style={{
                      padding: "6px 14px",
                      background: timelineZoom === z ? BRAND.green : "#fff",
                      color: timelineZoom === z ? "#fff" : BRAND.textDark,
                      border: `1px solid ${timelineZoom === z ? BRAND.green : BRAND.border}`,
                      borderRadius: 6, cursor: "pointer", fontWeight: 700, fontSize: 13,
                    }}
                  >{z}x</button>
                ))}
                <span style={{ width: 1, height: 22, background: BRAND.border, margin: "0 8px" }} />
                <span style={{ color: BRAND.textMuted, fontWeight: 700 }}>Chart:</span>
                {[
                  { id: "machine", label: "Actual" },
                  { id: "theoretical", label: "Theoretical" },
                ].map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setChartMode(c.id)}
                    style={{
                      padding: "6px 14px",
                      background: chartMode === c.id ? BRAND.green : "#fff",
                      color: chartMode === c.id ? "#fff" : BRAND.textDark,
                      border: `1px solid ${chartMode === c.id ? BRAND.green : BRAND.border}`,
                      borderRadius: 6, cursor: "pointer", fontWeight: 700, fontSize: 13,
                    }}
                  >{c.label}</button>
                ))}
              </div>

              {/* Big chart */}
              <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div style={{ overflowX: "auto", overflowY: "auto", flex: 1, border: `1px solid ${BRAND.border}`, borderRadius: 8, padding: 16, background: "#fafafa" }}>
                  <div style={{ width: `${timelineZoom * 100}%`, minWidth: "100%" }}>
                    {/* Hour ticks - bigger */}
                    <div style={{ position: "relative", height: 24, marginLeft: 200, marginBottom: 8, borderBottom: `1px solid ${BRAND.border}` }}>
                      {hourTicks.map((t) => (
                        <div key={t.ts} style={{
                          position: "absolute", left: pct(t.ts),
                          fontSize: 12, color: BRAND.textMuted, fontWeight: 700,
                          transform: "translateX(-50%)",
                        }}>{t.label}</div>
                      ))}
                    </div>
                    {/* Operator row — handling + breathing (bigger) */}
                    {(() => {
                      const opT = computeOperatorTimeline(group, viewFromMs, viewToMs, chartMode);
                      const totalHandleMs = opT.handles.reduce((a, b) => a + (b.end - b.start), 0);
                      const totalBreathMs = opT.breathing.reduce((a, b) => a + (b.end - b.start), 0);
                      const breathPct = Math.round((totalBreathMs / viewSpanMs) * 100);
                      return (
                        <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
                          <div style={{
                            width: 200, paddingRight: 12, fontSize: 14, fontWeight: 800,
                            color: BRAND.orange, whiteSpace: "nowrap",
                          }}>👤 Operator</div>
                          <div style={{
                            position: "relative", flex: 1, height: 28,
                            background: "#fce7f3", borderRadius: 6, overflow: "hidden",
                          }}>
                            {opT.breathing.map((b, i) => (
                              <div key={"ob-" + i}
                                onMouseEnter={(e) => {
                                  const r = e.currentTarget.getBoundingClientRect();
                                  setHoveredBlock({
                                    machine: "Operator",
                                    start: b.start, end: b.end,
                                    dur: b.end - b.start, kind: "breathing",
                                    x: r.left + r.width / 2, y: r.top,
                                  });
                                }}
                                onMouseLeave={() => setHoveredBlock(null)}
                                style={{
                                  position: "absolute", top: 0, bottom: 0,
                                  left: pct(b.start), width: wpct(b.end - b.start),
                                  background: "#fbcfe8", cursor: "pointer",
                                }} />
                            ))}
                            {opT.handles.map((h, i) => (
                              <div key={"oh-" + i}
                                onMouseEnter={(e) => {
                                  const r = e.currentTarget.getBoundingClientRect();
                                  setHoveredBlock({
                                    machine: "Operator",
                                    start: h.start, end: h.end,
                                    dur: h.end - h.start, kind: "handling",
                                    x: r.left + r.width / 2, y: r.top,
                                  });
                                }}
                                onMouseLeave={() => setHoveredBlock(null)}
                                style={{
                                  position: "absolute", top: 0, bottom: 0,
                                  left: pct(h.start), width: wpct(h.end - h.start),
                                  background: "#facc15", cursor: "pointer",
                                }} />
                            ))}
                          </div>
                          <div style={{ width: 200, paddingLeft: 12, fontSize: 12, color: BRAND.textMuted, lineHeight: 1.35, textAlign: "right" }}>
                            <div style={{ fontSize: 22, fontWeight: 900, color: "#ec4899", lineHeight: 1 }}>{breathPct}%</div>
                            <div style={{ fontSize: 10, color: BRAND.textMuted, fontWeight: 700, marginTop: 2 }}>breathing time</div>
                          </div>
                        </div>
                      );
                    })()}
                    {/* Bigger machine rows */}
                    {(() => {
                      const list = (group.per_machine || []).slice();
                      const firstMs = {};
                      if (chartMode === "theoretical") {
                        const theo = computeTheoretical(group, viewFromMs, viewToMs);
                        list.forEach((m) => {
                          const t = theo[m.device_id] || { handle: [], cycle: [] };
                          const all = t.handle.concat(t.cycle);
                          firstMs[m.device_id] = all.length ? Math.min(...all.map((b) => b.start)) : Infinity;
                        });
                      } else {
                        list.forEach((m) => {
                          const entry = (shiftActivity || []).find((s) => s.deviceId === m.device_id);
                          const ev = (entry?.events || []).filter((e) => e.ts >= viewFromMs && e.ts <= viewToMs);
                          firstMs[m.device_id] = ev.length ? ev[0].ts : Infinity;
                        });
                      }
                      list.sort((a, b) => firstMs[a.device_id] - firstMs[b.device_id]);
                      return list;
                    })().map((m) => {
                      let blocks, runBlocks;
                      if (chartMode === "theoretical") {
                        const t = computeTheoretical(group, viewFromMs, viewToMs)[m.device_id] || { handle: [], cycle: [], wait: [] };
                        blocks = t.handle.concat(t.wait);
                        runBlocks = t.cycle;
                      } else {
                        const entry = shiftActivity.find((s) => s.deviceId === m.device_id);
                        const { idle: idleAll, running: runAll } = machineBlocksFor(entry?.events || []);
                        blocks = idleAll.filter((b) => b.end > viewFromMs && b.start < viewToMs);
                        runBlocks = runAll.filter((b) => b.end > viewFromMs && b.start < viewToMs);
                      }
                      const totalIdleMs = blocks.reduce(
                        (a, b) => a + (Math.min(b.end, viewToMs) - Math.max(b.start, viewFromMs)), 0
                      );
                      const totalRunMs = runBlocks.reduce(
                        (a, b) => a + (Math.min(b.end, viewToMs) - Math.max(b.start, viewFromMs)), 0
                      );
                      const totalIdleMin = Math.round(totalIdleMs / 60000);
                      return (
                        <div key={m.device_id} style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                          <div style={{
                            width: 200, paddingRight: 12, fontSize: 14, fontWeight: 800,
                            color: BRAND.textDark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                          }} title={m.name}>{m.name}</div>
                          <div style={{
                            position: "relative", flex: 1, height: 40,
                            background: "#e5e7eb", borderRadius: 6, overflow: "hidden",
                          }}>
                            {/* Machining blocks — shown in Machine status + Theoretical modes */}
                            {(chartMode === "machine" || chartMode === "theoretical") && runBlocks.map((b, i) => (
                              <div
                                key={"run-" + i}
                                onMouseEnter={(e) => {
                                  const r = e.currentTarget.getBoundingClientRect();
                                  setHoveredBlock({
                                    machine: m.name, start: b.start, end: b.end,
                                    dur: b.end - b.start, kind: "machining",
                                    x: r.left + r.width / 2, y: r.top,
                                  });
                                }}
                                onMouseLeave={() => setHoveredBlock(null)}
                                style={{
                                  position: "absolute", top: 0, bottom: 0,
                                  left: pct(b.start), width: wpct(b.end - b.start),
                                  background: "#16a34a", cursor: "pointer",
                                }}
                              />
                            ))}
                            {blocks.map((b, i) => {
                              const dur = b.end - b.start;
                              const isHandle = b.kind === "handle";
                              const color = isHandle ? "#facc15" : "#3b82f6";
                              return (
                                <div
                                  key={i}
                                  onMouseEnter={(e) => {
                                    const r = e.currentTarget.getBoundingClientRect();
                                    setHoveredBlock({
                                      machine: m.name, start: b.start, end: b.end, dur, kind: b.kind,
                                      x: r.left + r.width / 2, y: r.top,
                                    });
                                  }}
                                  onMouseLeave={() => setHoveredBlock(null)}
                                  style={{
                                    position: "absolute", top: 0, bottom: 0,
                                    left: pct(b.start), width: wpct(dur),
                                    background: color, cursor: "pointer",
                                  }}
                                />
                              );
                            })}
                          </div>
                          {(() => {
                            const totalMs = viewSpanMs;
                            const utilPct = Math.round((totalRunMs / totalMs) * 100);
                            const utilColor = utilPct >= 80 ? BRAND.green : utilPct >= 50 ? "#f59e0b" : BRAND.red;
                            return (
                              <div style={{ width: 200, paddingLeft: 12, fontSize: 12, color: BRAND.textMuted, lineHeight: 1.35, textAlign: "right" }}>
                                <div style={{ fontSize: 22, fontWeight: 900, color: utilColor, lineHeight: 1 }}>
                                  {utilPct}%
                                </div>
                                <div style={{ fontSize: 10, color: BRAND.textMuted, fontWeight: 700, marginTop: 2 }}>utilization</div>
                                <div style={{ fontWeight: 700, color: BRAND.textDark, fontSize: 13, marginTop: 4 }}>
                                  {totalIdleMin}m idle · {Math.round(totalRunMs / 60000)}m machining
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Footer: legend + overload check */}
                <div style={{ marginTop: 12 }}>
                  {/* Operator-level summary stats (fullscreen only — minimal in inline) */}
                  {(() => {
                    let totalTransactions = 0;
                    let totalHandleMs = 0;
                    let totalAttention = 0;
                    group.per_machine.forEach((m) => {
                      let handleBlocks, waitBlocks;
                      if (chartMode === "theoretical") {
                        const t = computeTheoretical(group, viewFromMs, viewToMs)[m.device_id] || { handle: [], cycle: [], wait: [] };
                        handleBlocks = t.handle.filter((b) => b.end > viewFromMs && b.start < viewToMs);
                        waitBlocks = t.wait.filter((b) => b.end > viewFromMs && b.start < viewToMs);
                      } else {
                        const entry = (shiftActivity || []).find((s) => s.deviceId === m.device_id);
                        const { idle: idleAll } = machineBlocksFor(entry?.events || []);
                        const blocks = idleAll.filter((b) => b.end > viewFromMs && b.start < viewToMs);
                        handleBlocks = blocks.filter((b) => b.kind === "handle");
                        waitBlocks = blocks.filter((b) => b.kind === "wait");
                      }
                      totalTransactions += handleBlocks.length;
                      totalHandleMs += handleBlocks.reduce((a, b) =>
                        a + (Math.min(b.end, viewToMs) - Math.max(b.start, viewFromMs)), 0);
                      totalAttention += waitBlocks.length;
                    });
                    const N = group.per_machine.length;
                    const rounds = N > 0 ? totalTransactions / N : 0;
                    const walkSec = Math.round(rounds * (group.total_walk_seconds || 0));
                    const fmtMin = (ms) => {
                      const m = Math.round(ms / 60000);
                      if (m < 60) return `${m} min`;
                      return `${Math.floor(m/60)}h ${m%60}m`;
                    };
                    const fmtSec = (s) => {
                      if (s < 60) return `${s} s`;
                      const m = Math.floor(s / 60);
                      return s % 60 > 0 ? `${m}m ${s%60}s` : `${m} min`;
                    };
                    const opTL = computeOperatorTimeline(group, viewFromMs, viewToMs, chartMode);
                    const breathMs = opTL.breathing.reduce((a, b) => a + (b.end - b.start), 0);
                    const tiles = [
                      { label: "Transactions", value: totalTransactions, sub: "load + unload events", color: BRAND.textDark },
                      { label: "Handling time", value: fmtMin(totalHandleMs), sub: "operator at machine", color: "#a16207" },
                      { label: "Walking time", value: fmtSec(walkSec), sub: `${rounds.toFixed(1)} round(s)`, color: BRAND.textDark },
                      { label: "Breathing time", value: fmtMin(breathMs), sub: "operator free", color: "#ec4899" },
                      { label: "Attention events", value: totalAttention, sub: "machine waited >5 min", color: totalAttention === 0 ? BRAND.green : totalAttention < 5 ? "#a16207" : BRAND.red },
                    ];
                    return (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 12 }}>
                        {tiles.map((t) => (
                          <div key={t.label} style={{
                            background: "#f8fafc", border: `1px solid ${BRAND.border}`,
                            borderRadius: 8, padding: "12px 14px",
                          }}>
                            <div style={{ fontSize: 11, color: BRAND.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{t.label}</div>
                            <div style={{ fontSize: 24, fontWeight: 900, color: t.color, marginTop: 4, lineHeight: 1 }}>{t.value}</div>
                            <div style={{ fontSize: 11, color: BRAND.textMuted, marginTop: 4 }}>{t.sub}</div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                </div>
                <div style={{ marginTop: 12, display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", gap: 14, fontSize: 12, color: BRAND.textMuted, flexWrap: "wrap" }}>
                    {(chartMode === "machine" || chartMode === "theoretical") && (
                      <LegendDot color="#16a34a" label="Machining (machine running)" />
                    )}
                    <LegendDot color="#facc15" label="Loading / Unloading (≤5 min)" />
                    <LegendDot color="#3b82f6" label="Attention needed (>5 min)" />
                    <LegendDot color="#e5e7eb" label={chartMode === "machine" || chartMode === "theoretical" ? "Idle / no data" : "Machine running / no data"} />
                  </div>
                  <div style={{ flex: 1, minWidth: 320, padding: "10px 14px", background: "#f1f5f9", border: `1px solid ${BRAND.border}`, borderRadius: 6, fontSize: 13 }}>
                    <div style={{ fontWeight: 700, color: BRAND.textDark, marginBottom: 4 }}>Operator overload check</div>
                    <div style={{ color: BRAND.textMuted }}>
                      <b style={{ color: BRAND.textDark }}>{Math.round(overlapMs / 60000)} min</b>
                      {" "}({pctOfShift.toFixed(1)}% of shift) where 2+ machines needed attention
                      simultaneously, across <b>{overlapEvents}</b> moment{overlapEvents === 1 ? "" : "s"}.
                    </div>
                    <div style={{ marginTop: 4, color: pctOfShift < 5 ? BRAND.green : pctOfShift < 20 ? "#f59e0b" : BRAND.red, fontWeight: 700 }}>
                      {pctOfShift < 5 ? "Low — assignment is fine in practice" :
                       pctOfShift < 20 ? "Moderate — operator sometimes overloaded" :
                       "High — operator can't keep up; consider splitting"}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ fontSize: 11, color: BRAND.textMuted, textAlign: "center" }}>
                Click outside or press × to close
              </div>
            </div>
          </div>
        );
      })()}

      {/* Hover tooltip for timeline blocks — fixed positioned, follows the block */}
      {hoveredBlock && (() => {
        const fmtTime = (ms) => new Date(ms).toLocaleTimeString("en-IN", {
          timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
        });
        const fmtDur = (ms) => {
          const s = Math.round(ms / 1000);
          if (s < 60) return `${s} sec`;
          const m = Math.floor(s / 60);
          const sec = s % 60;
          if (m < 60) return sec > 0 ? `${m} min ${sec} sec` : `${m} min`;
          const h = Math.floor(m / 60);
          return `${h}h ${m % 60}m`;
        };
        const kindLabel = hoveredBlock.kind === "handle" ? "Loading / Unloading"
                         : hoveredBlock.kind === "wait" ? "Attention needed"
                         : hoveredBlock.kind === "machining" ? "Machining (running)"
                         : hoveredBlock.kind === "breathing" ? "Breathing time (operator free)"
                         : hoveredBlock.kind === "handling" ? "Handling (at machine)"
                         : "Idle";
        const kindColor = hoveredBlock.kind === "handle" ? "#fde68a"
                        : hoveredBlock.kind === "wait" ? "#93c5fd"
                        : hoveredBlock.kind === "machining" ? "#86efac"
                        : hoveredBlock.kind === "breathing" ? "#fbcfe8"
                        : hoveredBlock.kind === "handling" ? "#fde68a"
                        : "#cbd5e1";
        return (
          <div
            style={{
              position: "fixed",
              left: hoveredBlock.x,
              top: hoveredBlock.y - 12,
              transform: "translate(-50%, -100%)",
              background: "#1f2937",
              color: "#fff",
              padding: "10px 14px",
              borderRadius: 8,
              fontSize: 12,
              zIndex: 10000,
              pointerEvents: "none",
              boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
              whiteSpace: "nowrap",
              fontFamily: "system-ui, sans-serif",
            }}
          >
            <div style={{ fontWeight: 800, marginBottom: 6, fontSize: 13 }}>{hoveredBlock.machine}</div>
            <div style={{ display: "grid", gridTemplateColumns: "auto auto", columnGap: 12, rowGap: 3 }}>
              <span style={{ color: "#94a3b8" }}>Start</span>
              <span style={{ fontWeight: 700 }}>{fmtTime(hoveredBlock.start)}</span>
              <span style={{ color: "#94a3b8" }}>End</span>
              <span style={{ fontWeight: 700 }}>{fmtTime(hoveredBlock.end)}</span>
              <span style={{ color: "#94a3b8" }}>Duration</span>
              <span style={{ fontWeight: 700 }}>{fmtDur(hoveredBlock.dur)}</span>
            </div>
            <div style={{
              marginTop: 6, paddingTop: 6,
              borderTop: "1px solid rgba(255,255,255,0.15)",
              color: kindColor, fontWeight: 700,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: kindColor, display: "inline-block" }} />
              {kindLabel}
            </div>
            {/* Little arrow at the bottom pointing down to the block */}
            <div style={{
              position: "absolute", left: "50%", bottom: -6,
              transform: "translateX(-50%)",
              width: 0, height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: "6px solid #1f2937",
            }} />
          </div>
        );
      })()}
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ width: 12, height: 12, background: color, borderRadius: 2 }} />
      <span>{label}</span>
    </div>
  );
}

function StatTile({ icon, label, value, sub, accent }) {
  return (
    <div style={{
      background: "#fafafa",
      border: `1px solid ${accent || "#e5e7eb"}`,
      borderRadius: 8,
      padding: "10px 12px",
      textAlign: "center",
    }}>
      <div style={{ fontSize: 16, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: 10, color: "#6b7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: accent || "#111827", marginTop: 2 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ResultCard({ result }) {
  const v = VERDICT_COLORS[result.verdict] || VERDICT_COLORS.yellow;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Verdict banner */}
      <div
        style={{
          background: v.bg,
          border: `2px solid ${v.border}`,
          borderRadius: 12,
          padding: "16px 20px",
          display: "flex", alignItems: "center", gap: 14,
        }}
      >
        <div style={{ fontSize: 36, lineHeight: 1 }}>{v.emoji}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: v.text }}>
            {result.headline}
          </div>
          <div style={{ fontSize: 13, color: v.text, opacity: 0.85, marginTop: 4 }}>
            Walk loop: <b>{result.total_walk_m} m</b> ({fmtSeconds(result.total_walk_seconds)}) ·
            Service: <b>{fmtSeconds(result.service_seconds_per_cycle)}</b> ·
            Fastest cycle: <b>{fmtSeconds(result.fastest_machine_cycle_seconds)}</b>
          </div>
        </div>
      </div>

      {/* Walking sequence */}
      <div style={{
        background: "#fff", border: `1px solid ${BRAND.border}`,
        borderRadius: 8, padding: 14,
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: BRAND.textMuted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>
          Suggested walking sequence
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
          {result.walking_sequence.map((name, i) => (
            <React.Fragment key={`${name}-${i}`}>
              <div
                style={{
                  padding: "6px 12px",
                  background: BRAND.bg,
                  border: `1px solid ${BRAND.border}`,
                  borderRadius: 999,
                  fontSize: 13, fontWeight: 700,
                  color: BRAND.textDark,
                }}
              >
                {i + 1}. {name}
              </div>
              {i < result.walking_sequence.length - 1 && (
                <span style={{ color: BRAND.textMuted, fontSize: 16 }}>→</span>
              )}
            </React.Fragment>
          ))}
          <span style={{ color: BRAND.textMuted, fontSize: 16 }}>↻</span>
          <span style={{ color: BRAND.textMuted, fontSize: 12 }}>
            back to {result.walking_sequence[0]}
          </span>
        </div>
      </div>

      {/* Per-machine table */}
      <div style={{
        background: "#fff", border: `1px solid ${BRAND.border}`,
        borderRadius: 8, padding: 14,
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: BRAND.textMuted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>
          Per-machine cycle data
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", color: BRAND.textMuted, fontSize: 11, textTransform: "uppercase" }}>
              <th style={{ padding: "6px 8px" }}>Machine</th>
              <th style={{ padding: "6px 8px", textAlign: "right" }}>Cycle (median)</th>
              <th style={{ padding: "6px 8px", textAlign: "right" }}>Handling</th>
              <th style={{ padding: "6px 8px", textAlign: "right" }}>Samples</th>
            </tr>
          </thead>
          <tbody>
            {result.per_machine.map((m) => (
              <tr key={m.device_id} style={{ borderTop: `1px solid ${BRAND.border}` }}>
                <td style={{ padding: "8px", fontWeight: 600 }}>{m.name}</td>
                <td style={{ padding: "8px", textAlign: "right" }}>
                  {m.median_cycle_min != null ? `${m.median_cycle_min} min` : "—"}
                </td>
                <td style={{ padding: "8px", textAlign: "right" }}>
                  {m.median_handle_min != null ? `${m.median_handle_min} min` : "—"}
                </td>
                <td style={{ padding: "8px", textAlign: "right", color: BRAND.textMuted }}>
                  {m.samples}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Warnings */}
      {result.warnings?.length > 0 && (
        <div style={{
          background: "#fef3c7", border: `1px solid ${BRAND.amber}`,
          borderRadius: 8, padding: 12,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#7a4a00", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>
            ⚠️ Watch out for
          </div>
          {result.warnings.map((w, i) => (
            <div key={i} style={{ fontSize: 13, color: "#7a4a00", marginBottom: 4 }}>• {w}</div>
          ))}
        </div>
      )}

      {/* Notes */}
      {result.notes?.length > 0 && (
        <div style={{
          background: BRAND.bg, border: `1px solid ${BRAND.border}`,
          borderRadius: 8, padding: 12, fontSize: 12, color: BRAND.textMuted,
        }}>
          {result.notes.map((n, i) => (
            <div key={i}>• {n}</div>
          ))}
        </div>
      )}
    </div>
  );
}
